import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import './ChordSheetDisplay.css';
import { transposeChord, chordShapeForCapo, accidentalPreference, simplifyChord } from '../utils/chordTranspose';
import ChordDiagram from './ChordDiagram';
import { buildRomanNumeralMap } from '../utils/romanNumeral';
import { tokenizeLyricLine, buildRenderableSongModel, getWordDropSegments } from '../utils/alignmentLayout';
import { applyWordTimingEdit } from '../utils/timingEdit';

/**
 * Renders an interactive chord sheet.
 *
 * Editing gestures:
 *   • Double-click a word  → open inline input above it to add/edit chord
 *   • Double-click a chord → open inline input to edit/delete (blank = delete)
 *   • Drag a chord         → drop onto any word to move the chord there
 *
 * alignment: [{chord, lineIndex, wordIndex}]  (raw, pre-transpose)
 * onAlignmentChange: (newAlignment) => void
 */
function ChordSheetDisplay({
  lyrics,
  alignment,
  transposition,
  capo,
  songKey,
  simplified,
  onAlignmentChange,
  activePosition,
  currentTime,
  showAnalysis,
  rawAnalysis,
  timedLyrics,
  onTimedLyricsChange,
  bpm = null,
  highlightWordsDuringPlayback = true,
  timingEditMode = false,
  autoFollowPlayback = true,
}) {
  const pref = accidentalPreference(songKey);

  // ── Edit / drag state ────────────────────────────────────────────────────
  const [editTarget, setEditTarget] = useState(null);
  // { lineIndex, wordIndex, initialValue: string, isNew: bool }

  const [dragSource, setDragSource] = useState(null);
  // { lineIndex, wordIndex, rawChord: string, displayedChord: string }
  const dragSourceRef = useRef(null);
  const lastDragSourceRef = useRef(null);
  const preDragSourceRef = useRef(null);
  const dropInFlightRef = useRef(false);
  const [dragActive, setDragActive] = useState(false);
  const dragTxnIdRef = useRef(0);
  const committedTxnRef = useRef(0);
  const abortedLyricDropCountRef = useRef(0);

  const [dropTarget, setDropTarget] = useState(null);
  // { lineIndex, wordIndex }
  const dropTargetRef = useRef(null);

  // ── Hover state for chord diagram ────────────────────────────────────────
  const [hoveredChord, setHoveredChord] = useState(null); // { chord, rect }

  // ── Alternatives context menu ────────────────────────────────────────────
  const [altMenu, setAltMenu] = useState(null); // { lineIndex, wordIndex, rawChord, alts, x, y }
  const [wordTimingMenu, setWordTimingMenu] = useState(null); // { x, y, lineIndex, wordIndex, word, start, end, duration, matchedWhisperWord }
  const [selectedTimingWord, setSelectedTimingWord] = useState(null); // { lineIndex, wordIndex }
  const [dragTimingHandle, setDragTimingHandle] = useState(null); // { lineIndex, wordIndex, handle, min, max }
  const timingUndoSnapshotRef = useRef(null);
  const [hoverWordKey, setHoverWordKey] = useState(null); // `${lineIndex}:${wordIndex}`
  const lineRefs = useRef({});
  const autoFollowPauseUntilRef = useRef(0);

  const editInputRef = useRef(null);

  useEffect(() => {
    if (editTarget && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editTarget]);

  // ── Alternatives lookup (from rawAnalysis.chords) ───────────────────────
  const alternativesMap = useMemo(() => {
    if (!rawAnalysis?.chords) return {};
    // Build map from raw chord name → alternatives list
    const map = {};
    for (const c of rawAnalysis.chords) {
      if (c.alternatives?.length && c.chord) {
        map[c.chord] = c.alternatives;
      }
    }
    return map;
  }, [rawAnalysis?.chords]);

  const effectiveTimedLyrics = useMemo(
    () => (timedLyrics && timedLyrics.length ? timedLyrics : (rawAnalysis?.timedLyrics || [])),
    [timedLyrics, rawAnalysis?.timedLyrics]
  );

  // ── Word time lookup (from rawAnalysis.timedLyrics) ─────────────────────
  // Maps "lineIndex:wordIndex" → start time (seconds) for precise chord timing
  const wordTimeMap = useMemo(() => {
    const map = {};
    for (const w of (effectiveTimedLyrics || [])) {
      if (w.start != null) {
        map[`${w.lineIndex}:${w.wordIndex}`] = w.start;
      }
    }
    return map;
  }, [effectiveTimedLyrics]);

  const wordTimingLookup = useMemo(() => {
    const map = {};
    for (const w of (effectiveTimedLyrics || [])) {
      map[`${w.lineIndex}:${w.wordIndex}`] = w;
    }
    return map;
  }, [effectiveTimedLyrics]);

  const timedWordsByLine = useMemo(() => {
    const byLine = {};
    for (const w of (effectiveTimedLyrics || [])) {
      if (w.lineIndex == null || w.wordIndex == null || w.start == null) continue;
      const li = Number(w.lineIndex);
      if (!byLine[li]) byLine[li] = [];
      const start = Number(w.start);
      const endV = Number(w.end);
      byLine[li].push({
        ...w,
        start,
        end: Number.isFinite(endV) ? Math.max(start, endV) : start,
      });
    }
    Object.keys(byLine).forEach((k) => byLine[k].sort((a, b) => a.wordIndex - b.wordIndex));
    return byLine;
  }, [effectiveTimedLyrics]);

  const sixteenthSec = useMemo(() => {
    const b = Number(bpm ?? rawAnalysis?.bpm);
    return Number.isFinite(b) && b > 0 ? (60 / b / 4) : 0.125;
  }, [bpm, rawAnalysis?.bpm]);
  const edgeSlotOffsetSec = sixteenthSec;
  const EXACT_MATCH_EPS_SEC = 0.01;

  useEffect(() => {
    if (!wordTimingMenu) return undefined;
    const closeMenu = () => setWordTimingMenu(null);
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeMenu();
    };
    document.addEventListener('mousedown', closeMenu);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', closeMenu);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [wordTimingMenu]);

  useEffect(() => {
    if (timingEditMode) return;
    setSelectedTimingWord(null);
    setDragTimingHandle(null);
    setWordTimingMenu(null);
  }, [timingEditMode]);

  useEffect(() => {
    if (!autoFollowPlayback) return undefined;
    const scroller = document.querySelector('.right-panel__content');
    if (!scroller) return undefined;
    const markPaused = () => { autoFollowPauseUntilRef.current = Date.now() + 1400; };
    scroller.addEventListener('wheel', markPaused, { passive: true });
    scroller.addEventListener('touchmove', markPaused, { passive: true });
    return () => {
      scroller.removeEventListener('wheel', markPaused);
      scroller.removeEventListener('touchmove', markPaused);
    };
  }, [autoFollowPlayback]);


  // Chord position key: combines wordIndex + syllableOffset for sub-word placement
  const posKey = (wordIndex, syllableOffset = 0) => `${wordIndex}:${syllableOffset}`;

  // Full display alignment (with transpose + capo applied)
  const displayAlignment = useMemo(() => {
    return (alignment || []).map(({ chord, ...rest }) => {
      if (!chord) return { ...rest, chord };
      const base = simplified ? simplifyChord(chord) : chord;
      const transposed = transposeChord(base, transposition, pref);
      const displayed = capo > 0 ? chordShapeForCapo(transposed, capo) : transposed;
      return { ...rest, chord: displayed };
    });
  }, [alignment, transposition, capo, pref, simplified]);

  const canonicalModel = useMemo(
    () => buildRenderableSongModel(lyrics || '', displayAlignment),
    [lyrics, displayAlignment]
  );
  const canonicalRowByLine = useMemo(() => {
    const map = {};
    canonicalModel.forEach((row) => {
      if (row?.lineIndex == null) return;
      map[row.lineIndex] = row;
    });
    return map;
  }, [canonicalModel]);

  // Canonical per-line chord map from shared layout engine (web/text/pdf parity).
  const canonicalChordMap = useMemo(() => {
    const map = {};
    canonicalModel.forEach((row) => {
      if (row.type !== 'lyric' || row.lineIndex == null) return;
      const lineMap = {};
      (row.alignedEvents || []).forEach((evt) => {
        if (!evt?.chord) return;
        const pk = posKey(evt.wordIndex, evt.syllableOffset || 0);
        lineMap[pk] = evt.chord;
      });
      map[row.lineIndex] = lineMap;
    });
    return map;
  }, [canonicalModel]);

  // Spacer key set from canonical model events.
  const spacerMap = useMemo(() => {
    const map = {};
    canonicalModel.forEach((row) => {
      if (row.type !== 'lyric' || row.lineIndex == null) return;
      const li = row.lineIndex;
      (row.alignedEvents || []).forEach((evt) => {
        const isSpacer = Boolean(evt.isSpacer) || !Number.isInteger(Number(evt.wordIndex));
        if (!isSpacer) return;
        if (!map[li]) map[li] = { keys: new Set(), sorted: [] };
        const pk = posKey(evt.wordIndex, evt.syllableOffset || 0);
        if (map[li].keys.has(pk)) return;
        map[li].keys.add(pk);
        map[li].sorted.push({ pk, wordIndex: Number(evt.wordIndex) });
      });
    });
    Object.keys(map).forEach((li) => {
      map[li].sorted.sort((a, b) => a.wordIndex - b.wordIndex);
    });
    return map;
  }, [canonicalModel]);

  // Real chord map: chords that are explicitly in alignment (not carry-forward).
  // Used to distinguish real vs virtual chords and to reverse-look-up raw values.
  // Suppression sentinels (chord: null) are excluded — they're not renderable chords.
  const realChordMap = useMemo(() => {
    const map = {};
    (alignment || []).forEach(({ chord, lineIndex, wordIndex, syllableOffset = 0 }) => {
      if (!chord) return; // skip suppression sentinels
      if (!map[lineIndex]) map[lineIndex] = {};
      map[lineIndex][posKey(wordIndex, syllableOffset)] = chord; // raw (pre-transpose)
    });
    return map;
  }, [alignment]);

  // Time map: lookup timing for each alignment entry
  const timeMap = useMemo(() => {
    const map = {};
    (alignment || []).forEach(({ lineIndex, wordIndex, syllableOffset = 0, time }) => {
      if (time == null) return;
      if (!map[lineIndex]) map[lineIndex] = {};
      map[lineIndex][posKey(wordIndex, syllableOffset)] = time;
    });
    return map;
  }, [alignment]);

  const lines = useMemo(() => (lyrics || '').split('\n'), [lyrics]);

  // Sorted real chord events with timing — used for active-chord window computation
  const sortedTimedEntries = useMemo(() =>
    (alignment || []).filter(e => e.time != null).sort((a, b) => a.time - b.time),
  [alignment]);
  const activeWordLineFromTiming = useMemo(() => {
    if (currentTime == null) return null;
    const words = (effectiveTimedLyrics || [])
      .filter((w) => w?.start != null && w?.lineIndex != null)
      .map((w) => {
        const start = Number(w.start);
        const endV = Number(w.end);
        const end = Number.isFinite(endV) ? Math.max(start, endV) : (start + 0.12);
        return { lineIndex: Number(w.lineIndex), start, end };
      })
      .filter((w) => Number.isFinite(w.start) && Number.isFinite(w.end));
    if (!words.length) return null;

    // Primary: choose the most recently-started currently active word.
    let activeNow = null;
    for (const w of words) {
      if (currentTime >= w.start && currentTime <= (w.end + 0.03)) {
        if (!activeNow || w.start > activeNow.start) activeNow = w;
      }
    }
    if (activeNow) return activeNow.lineIndex;

    // Smoothing: hold previous word's line shortly between words.
    let prev = null;
    for (const w of words) {
      if (w.start <= currentTime && (!prev || w.start > prev.start)) prev = w;
    }
    if (prev && (currentTime - prev.end) <= 0.35) return prev.lineIndex;

    // Near-future snap: if we're about to enter next word, bias to it.
    let next = null;
    for (const w of words) {
      if (w.start > currentTime && (!next || w.start < next.start)) next = w;
    }
    if (next && (next.start - currentTime) <= 0.1) return next.lineIndex;

    return null;
  }, [currentTime, effectiveTimedLyrics]);
  const hasTimedWords = useMemo(
    () => (effectiveTimedLyrics || []).some((w) => w?.start != null && w?.lineIndex != null),
    [effectiveTimedLyrics]
  );
  const activeLineForFollow = hasTimedWords
    ? activeWordLineFromTiming
    : (activePosition?.lineIndex ?? null);
  const followScrollTargetLine = useMemo(() => {
    if (activeLineForFollow == null || activeLineForFollow < 0) return null;
    let remainingAhead = 2;
    for (let i = activeLineForFollow + 1; i < lines.length; i++) {
      const t = (lines[i] || '').trim();
      if (!t || /^\[.+\]$/.test(t)) continue;
      remainingAhead -= 1;
      if (remainingAhead <= 0) return i;
    }
    return activeLineForFollow;
  }, [activeLineForFollow, lines]);

  // Canonical display map (already de-duplicated/ordered by shared layout model)
  const enrichedChordMap = useMemo(() => {
    return canonicalChordMap;
  }, [canonicalChordMap]);

  // Single active chord key "li:wi:so" — the one chord cell to highlight during playback.
  // Simple time-vector approach: all chords have explicit timing, so binary-search
  // sortedTimedEntries for the last event at or before currentTime. That chord stays
  // highlighted until the next chord event fires — no carry-forward scanning needed.
  const visualActiveKey = useMemo(() => {
    if (currentTime == null || !sortedTimedEntries.length) return null;
    let lo = 0, hi = sortedTimedEntries.length - 1, found = null;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (sortedTimedEntries[mid].time <= currentTime) {
        found = sortedTimedEntries[mid];
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    if (!found) return null;
    return `${found.lineIndex}:${found.wordIndex}:${found.syllableOffset ?? 0}`;
  }, [currentTime, sortedTimedEntries]);

  useEffect(() => {
    if (!autoFollowPlayback) return;
    if (followScrollTargetLine == null || followScrollTargetLine < 0) return;
    if (Date.now() < autoFollowPauseUntilRef.current) return;
    const scroller = document.querySelector('.right-panel__content');
    const target = lineRefs.current[followScrollTargetLine];
    if (!scroller || !target) return;

    const scrollerRect = scroller.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const centerY = scrollerRect.top + (scrollerRect.height / 2);
    const deadband = scrollerRect.height * 0.18;
    const targetMid = targetRect.top + (targetRect.height / 2);
    if (Math.abs(targetMid - centerY) <= deadband) return;

    const current = scroller.scrollTop;
    const delta = targetMid - centerY;
    scroller.scrollTo({ top: Math.max(0, current + delta), behavior: 'smooth' });
  }, [followScrollTargetLine, currentTime, autoFollowPlayback]);

  // ── Roman numeral analysis map ──────────────────────────────────────────
  const romanMap = useMemo(() => {
    if (!showAnalysis || !songKey) return new Map();
    return buildRomanNumeralMap(
      (alignment || []).reduce((acc, { lineIndex, wordIndex, syllableOffset = 0, chord }) => {
        if (!acc[lineIndex]) acc[lineIndex] = { chords: {} };
        acc[lineIndex].chords[posKey(wordIndex, syllableOffset)] = chord;
        return acc;
      }, []),
      songKey,
      transposition,
      transposeChord,
    );
  }, [showAnalysis, songKey, alignment, transposition]);

  // ── Reverse-transpose helper ─────────────────────────────────────────────
  // Convert a displayed chord (post-transpose+capo) back to raw storage value.
  const displayedToRaw = useCallback((displayed) => {
    if (!displayed) return displayed;
    // Un-capo: the shape was transposed -capo, so add capo back
    const withoutCapo = capo > 0 ? transposeChord(displayed, +capo, pref) : displayed;
    // Un-transpose
    return transposeChord(withoutCapo, -(transposition || 0), pref);
  }, [capo, transposition, pref]);

  // ── Helpers: infer timing for a position ────────────────────────────────
  // When we have no time for a chord (carry-forward drag or new edit), estimate
  // by interpolating between the nearest timed entries in the alignment.
  // Pass `fromAlignment` explicitly when the default `alignment` prop shouldn't be used
  // (e.g. after removing the source entry during a drag).
  const inferTime = useCallback((lineIndex, wordIndex, syllableOffset = 0, fromAlignment = alignment, lineTokens = null) => {
    const wiNum = Number(wordIndex);
    const soNum = Math.max(0, Number(syllableOffset || 0));
    const PRE_POST_GAP_SEC = edgeSlotOffsetSec;

    // Slot-level timing for in-word anchors using timed lyrics + tokenized segments.
    if (Number.isInteger(wiNum)) {
      const timing = wordTimingLookup[`${lineIndex}:${wiNum}`];
      if (timing?.start != null) {
        const start = Number(timing.start);
        const endV = Number(timing.end);
        const end = Number.isFinite(endV) ? Math.max(start, endV) : (start + 0.12);
        const dur = Math.max(0.001, end - start);
        if (soNum <= 0) return start;

        const tokens = Array.isArray(lineTokens) && lineTokens.length
          ? lineTokens
          : (canonicalRowByLine[lineIndex]?.tokens || []);
        const wordSegs = (tokens || [])
          .filter((t) => t?.isWord && Number(t.wordIndex) === wiNum)
          .sort((a, b) => Number(a.syllableOffset || 0) - Number(b.syllableOffset || 0));
        if (wordSegs.length > 1) {
          const totalChars = Math.max(1, wordSegs.reduce((s, seg) => s + String(seg.text || '').length, 0));
          const stopSeg = Math.min(soNum, wordSegs.length - 1);
          let consumed = 0;
          for (let i = 0; i < stopSeg; i++) consumed += String(wordSegs[i].text || '').length;
          const p = Math.max(0, Math.min(1, consumed / totalChars));
          return start + (p * dur);
        }

        // If slot offset exists but no split segments are visible, place inside word.
        const fallbackP = Math.max(0.05, Math.min(0.95, soNum / (soNum + 1)));
        return start + (fallbackP * dur);
      }
    }

    if (!Number.isInteger(wiNum) && Number.isFinite(wiNum)) {
      const leftIdx = Math.floor(wiNum);
      const rightIdx = Math.ceil(wiNum);
      const left = wordTimingLookup[`${lineIndex}:${leftIdx}`];
      const right = wordTimingLookup[`${lineIndex}:${rightIdx}`];
      const leftEnd = left?.end != null ? Number(left.end) : null;
      const rightStart = right?.start != null ? Number(right.start) : null;

      if (!Number.isFinite(leftEnd) && Number.isFinite(rightStart)) {
        return Math.max(0, rightStart - PRE_POST_GAP_SEC);
      }
      if (Number.isFinite(leftEnd) && !Number.isFinite(rightStart)) {
        return leftEnd + PRE_POST_GAP_SEC;
      }
      if (Number.isFinite(leftEnd) && Number.isFinite(rightStart)) {
        if (rightStart >= leftEnd) {
          return leftEnd + 0.5 * (rightStart - leftEnd);
        }
        return leftEnd;
      }
    }

    // First preference: use the Whisper-aligned word start time directly
    const wordKey = `${lineIndex}:${wordIndex}`;
    if (wordTimeMap[wordKey] != null) return wordTimeMap[wordKey];

    const timedEntries = (fromAlignment || []).filter(e => e.time != null);
    if (timedEntries.length === 0) return undefined;

    // Build word-order index for all lines
    const wordOrder = timedEntries
      .map(e => ({ ...e, order: e.lineIndex * 10000 + e.wordIndex }))
      .sort((a, b) => a.order - b.order);

    const targetOrder = lineIndex * 10000 + wordIndex;
    // Find neighbours
    let before = null, after = null;
    for (const e of wordOrder) {
      if (e.order <= targetOrder) before = e;
      else if (!after) after = e;
    }
    if (before && after) {
      // Interpolate proportionally between before/after rather than simple midpoint
      const span = after.order - before.order;
      const ratio = span > 0 ? (targetOrder - before.order) / span : 0.5;
      return before.time + ratio * (after.time - before.time);
    }
    if (before) return before.time;
    if (after) return after.time;
    return undefined;
  }, [alignment, wordTimeMap, wordTimingLookup, canonicalRowByLine, edgeSlotOffsetSec]);

  const resolveDropTime = useCallback(({
    lineIndex,
    targetWordIndex,
    targetSyllableOffset = 0,
    slotKind = 'word',
    anchorProgress = null,
    gapProgress = null,
    gapStartTime = null,
    gapEndTime = null,
    sourceTime = null,
    fromAlignment = alignment,
    lineTokens = null,
  }) => {
    const lineWords = [...(timedWordsByLine[lineIndex] || [])]
      .filter((w) => w?.start != null)
      .sort((a, b) => Number(a.wordIndex) - Number(b.wordIndex));
    const first = lineWords.length ? lineWords[0] : null;
    const last = lineWords.length ? lineWords[lineWords.length - 1] : null;
    const lastEnd = last
      ? (Number.isFinite(Number(last.end)) ? Number(last.end) : Number(last.start))
      : null;

    const wi = Number(targetWordIndex);
    let resolved = null;

    if (slotKind === 'edge-pre' && first) {
      resolved = Math.max(0, Number(first.start) - edgeSlotOffsetSec);
    } else if (slotKind === 'edge-post' && lastEnd != null) {
      resolved = Number(lastEnd) + edgeSlotOffsetSec;
    } else if (slotKind === 'word' || slotKind === 'subword') {
      if (Number.isInteger(wi)) {
        const timing = wordTimingLookup[`${lineIndex}:${wi}`];
        if (timing?.start != null) {
          const start = Number(timing.start);
          const endVal = Number(timing.end);
          const end = Number.isFinite(endVal) ? Math.max(start, endVal) : (start + 0.12);
          const dur = Math.max(0.001, end - start);
          if (slotKind === 'word') resolved = start;
          else if (Number.isFinite(Number(anchorProgress))) {
            resolved = start + (Math.max(0, Math.min(1, Number(anchorProgress))) * dur);
          } else {
            resolved = inferTime(lineIndex, wi, targetSyllableOffset, fromAlignment, lineTokens);
          }
        }
      }
    } else if (slotKind === 'gap') {
      const explicitGapStart = Number.isFinite(Number(gapStartTime)) ? Number(gapStartTime) : null;
      const explicitGapEnd = Number.isFinite(Number(gapEndTime)) ? Number(gapEndTime) : null;
      if (explicitGapStart != null && explicitGapEnd != null && explicitGapEnd >= explicitGapStart) {
        const gp = Number.isFinite(Number(gapProgress)) ? Math.max(0, Math.min(1, Number(gapProgress))) : 0.5;
        resolved = explicitGapStart + (gp * (explicitGapEnd - explicitGapStart));
      }
      if (resolved != null) {
        if (resolved != null && Number.isFinite(Number(sourceTime)) && Math.abs(Number(sourceTime) - Number(resolved)) <= EXACT_MATCH_EPS_SEC) {
          return Number(sourceTime);
        }
        return resolved;
      }
      const leftIdx = Math.floor(wi);
      const rightIdx = Math.ceil(wi);
      const directLeft = wordTimingLookup[`${lineIndex}:${leftIdx}`];
      const directRight = wordTimingLookup[`${lineIndex}:${rightIdx}`];
      const nearestLeft = directLeft || [...lineWords]
        .filter((w) => Number(w.wordIndex) <= leftIdx)
        .sort((a, b) => Number(b.wordIndex) - Number(a.wordIndex))[0];
      const nearestRight = directRight || [...lineWords]
        .filter((w) => Number(w.wordIndex) >= rightIdx)
        .sort((a, b) => Number(a.wordIndex) - Number(b.wordIndex))[0];
      const leftEnd = nearestLeft?.end != null ? Number(nearestLeft.end) : null;
      const rightStart = nearestRight?.start != null ? Number(nearestRight.start) : null;
      if (Number.isFinite(leftEnd) && Number.isFinite(rightStart) && rightStart >= leftEnd) {
        const gp = Number.isFinite(Number(gapProgress)) ? Math.max(0, Math.min(1, Number(gapProgress))) : 0.5;
        resolved = leftEnd + (gp * (rightStart - leftEnd));
      } else if (Number.isFinite(leftEnd) && !Number.isFinite(rightStart)) {
        resolved = leftEnd + edgeSlotOffsetSec;
      } else if (!Number.isFinite(leftEnd) && Number.isFinite(rightStart)) {
        resolved = Math.max(0, rightStart - edgeSlotOffsetSec);
      }
    }

    if (resolved == null) {
      resolved = inferTime(lineIndex, targetWordIndex, targetSyllableOffset, fromAlignment, lineTokens);
    }

    if (resolved != null && Number.isFinite(Number(sourceTime)) && Math.abs(Number(sourceTime) - Number(resolved)) <= EXACT_MATCH_EPS_SEC) {
      return Number(sourceTime);
    }
    return resolved;
  }, [alignment, timedWordsByLine, wordTimingLookup, edgeSlotOffsetSec, inferTime]);

  const getGapBounds = useCallback((lineIndex, fractionalWordIndex) => {
    const wi = Number(fractionalWordIndex);
    if (!Number.isFinite(wi)) return null;
    const leftIdx = Math.floor(wi);
    const rightIdx = Math.ceil(wi);
    const lineWords = [...(timedWordsByLine[lineIndex] || [])]
      .filter((w) => w?.start != null)
      .sort((a, b) => Number(a.wordIndex) - Number(b.wordIndex));
    const directLeft = wordTimingLookup[`${lineIndex}:${leftIdx}`];
    const directRight = wordTimingLookup[`${lineIndex}:${rightIdx}`];
    const nearestLeft = directLeft || [...lineWords]
      .filter((w) => Number(w.wordIndex) <= leftIdx)
      .sort((a, b) => Number(b.wordIndex) - Number(a.wordIndex))[0];
    const nearestRight = directRight || [...lineWords]
      .filter((w) => Number(w.wordIndex) >= rightIdx)
      .sort((a, b) => Number(a.wordIndex) - Number(b.wordIndex))[0];
    const start = nearestLeft?.end != null ? Number(nearestLeft.end) : null;
    const end = nearestRight?.start != null ? Number(nearestRight.start) : null;
    if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return null;
    return { gapStartTime: start, gapEndTime: end };
  }, [timedWordsByLine, wordTimingLookup]);

  const makeDropPayload = useCallback(({
    lineIndex,
    wordIndex,
    syllableOffset = 0,
    slotKind = 'word',
    anchorProgress = null,
    gapProgress = null,
    slotId = null,
    slotMeta = null,
    routeContext = 'lyric',
  }) => ({
    lineIndex,
    wordIndex,
    syllableOffset,
    slotKind,
    anchorProgress,
    gapProgress,
    slotKey: slotId,
    slotId,
    slotMeta,
    routeContext,
    txnId: dragTxnIdRef.current,
  }), []);

  // ── Edit handlers ────────────────────────────────────────────────────────
  const commitEdit = useCallback((inputValue) => {
    if (!editTarget) return;
    if (!onAlignmentChange) { setEditTarget(null); return; }

    const { lineIndex, wordIndex, syllableOffset = 0, isNew } = editTarget;
    const trimmed = inputValue.trim();
    const sectionLabel = String(lines?.[lineIndex] || '').trim();
    const isStandaloneSlot = Number(lineIndex) < 0 || /^\[(intro|outro|instrumental|solo|guitar\s+solo)\b/i.test(sectionLabel);

    // Comma-separated chord insertion for all contexts.
    if (trimmed && trimmed.includes(',')) {
      const parts = trimmed.split(',').map(s => s.trim()).filter(Boolean);
      if (parts.length > 1) {
        if (!isStandaloneSlot) {
          const baseFiltered = (alignment || []).filter(
            e => !(e.lineIndex === lineIndex && e.wordIndex === wordIndex && (e.syllableOffset ?? 0) === syllableOffset)
          );
          const occupied = new Set(
            baseFiltered
              .filter((e) => Number(e.lineIndex) === Number(lineIndex) && e.chord != null)
              .map((e) => `${Number(e.wordIndex)}:${Number(e.syllableOffset || 0)}`)
          );
          const lineWords = [...(timedWordsByLine[lineIndex] || [])]
            .filter((w) => w?.wordIndex != null)
            .sort((a, b) => Number(a.wordIndex) - Number(b.wordIndex));
          const forwardWordTargets = [...new Set(lineWords.map((w) => Number(w.wordIndex)).filter((n) => Number.isInteger(n) && n > Number(wordIndex)))]
            .map((wi) => ({ wordIndex: wi, syllableOffset: 0, slotKind: 'word', gapProgress: null }));
          const insertionTargets = [
            { wordIndex, syllableOffset, slotKind: Number(syllableOffset || 0) > 0 ? 'subword' : 'word', gapProgress: null },
            ...forwardWordTargets,
            { wordIndex: Number(wordIndex) + 0.5, syllableOffset: 0, slotKind: 'edge-post', gapProgress: 1 },
          ];

          const newEntries = [];
          for (let i = 0; i < parts.length; i++) {
            let target = insertionTargets[i];
            if (i > 0) {
              for (let t = i; t < insertionTargets.length; t++) {
                const cand = insertionTargets[t];
                const key = `${Number(cand.wordIndex)}:${Number(cand.syllableOffset || 0)}`;
                if (!occupied.has(key)) { target = cand; break; }
              }
            }
            if (!target) continue;
            const raw = displayedToRaw(parts[i]);
            const lineTokens = canonicalRowByLine[lineIndex]?.tokens || null;
            const time = resolveDropTime({
              lineIndex,
              targetWordIndex: target.wordIndex,
              targetSyllableOffset: target.syllableOffset || 0,
              slotKind: target.slotKind,
              anchorProgress: null,
              gapProgress: target.gapProgress,
              sourceTime: null,
              fromAlignment: baseFiltered,
              lineTokens,
            });
            const entry = { chord: raw, lineIndex, wordIndex: target.wordIndex };
            if ((target.syllableOffset || 0) > 0) entry.syllableOffset = target.syllableOffset;
            if (target.slotKind === 'edge-pre') entry.anchorRule = 'manual-slot-drop-pre-edge';
            else if (target.slotKind === 'edge-post') entry.anchorRule = 'manual-slot-drop-post-edge';
            else entry.anchorRule = 'manual-slot-drop';
            if (Number.isFinite(Number(target.gapProgress))) entry.gapProgress = Number(target.gapProgress);
            if (time != null) entry.time = Number(Number(time).toFixed(3));
            newEntries.push(entry);
            occupied.add(`${Number(target.wordIndex)}:${Number(target.syllableOffset || 0)}`);
          }
          onAlignmentChange([...baseFiltered, ...newEntries]);
          setEditTarget(null);
          return;
        }

        // Standalone positions (intro/outro/instrumental header) insertion by shifting explicit slots.
        const extraSlots = parts.length - 1; // number of new slots needed after current wi
        const originalTime = (alignment || []).find(e => e.lineIndex === lineIndex && e.wordIndex === wordIndex)?.time;
        // Shift all entries at wordIndex > current wi up by extraSlots to make room
        const shifted = (alignment || [])
          .filter(e => !(e.lineIndex === lineIndex && e.wordIndex === wordIndex))
          .map(e => (e.lineIndex === lineIndex && e.wordIndex > wordIndex)
            ? { ...e, wordIndex: e.wordIndex + extraSlots }
            : e
          );
        const newEntries = parts.map((chord, idx) => {
          const raw = displayedToRaw(chord);
          const entry = { chord: raw, lineIndex, wordIndex: wordIndex + idx };
          if (idx === 0 && originalTime != null) entry.time = originalTime;
          return entry;
        });
        onAlignmentChange([...shifted, ...newEntries]);
        setEditTarget(null);
        return;
      }
    }

    let newAlignment;
    if (trimmed) {
      const raw = displayedToRaw(trimmed);
      const existing = (alignment || []).find(e => e.lineIndex === lineIndex && e.wordIndex === wordIndex && (e.syllableOffset ?? 0) === syllableOffset);
      const withoutOld = (alignment || []).filter(
        e => !(e.lineIndex === lineIndex && e.wordIndex === wordIndex && (e.syllableOffset ?? 0) === syllableOffset)
      );
    const lineTokens = canonicalRowByLine[lineIndex]?.tokens || null;
    const time = existing?.time ?? inferTime(lineIndex, wordIndex, syllableOffset, alignment, lineTokens);
      const newEntry = { chord: raw, lineIndex, wordIndex };
      if (syllableOffset > 0) newEntry.syllableOffset = syllableOffset;
      if (time != null) newEntry.time = time;
      newAlignment = [...withoutOld, newEntry];
    } else if (!isNew) {
      newAlignment = (alignment || []).filter(
        e => !(e.lineIndex === lineIndex && e.wordIndex === wordIndex && (e.syllableOffset ?? 0) === syllableOffset)
      );
    } else {
      newAlignment = alignment || [];
    }

    onAlignmentChange(newAlignment);
    setEditTarget(null);
  }, [editTarget, alignment, onAlignmentChange, displayedToRaw, inferTime, canonicalRowByLine, timedWordsByLine, resolveDropTime, lines]);

  const openEdit = useCallback((li, wi, so, initialValue, isNew, opts = {}) => {
    setEditTarget({
      lineIndex: li,
      wordIndex: wi,
      syllableOffset: so,
      initialValue,
      isNew,
      anchorCol: Number.isFinite(Number(opts.anchorCol)) ? Number(opts.anchorCol) : null,
    });
  }, []);

  const editInputSizing = useCallback((value) => {
    const len = String(value || '').length;
    const widthCh = Math.max(5, len + 2);
    return {
      size: widthCh,
      style: { '--edit-size-ch': widthCh, '--edit-min-ch': Math.max(3, len + 1) },
    };
  }, []);

  // Pin a carry-forward chord immediately (no dialog) — assigns the word's time
  const pinCarryForward = useCallback((li, wi, so, displayedChord) => {
    if (!onAlignmentChange) return;
    const raw = displayedToRaw(displayedChord);
    const lineTokens = canonicalRowByLine[li]?.tokens || null;
    const time = inferTime(li, wi, so, alignment, lineTokens);
    const entry = { chord: raw, lineIndex: li, wordIndex: wi };
    if (so > 0) entry.syllableOffset = so;
    if (time != null) entry.time = time;
    // Remove any existing entry OR suppression sentinel at this position
    const without = (alignment || []).filter(
      e => !(e.lineIndex === li && e.wordIndex === wi && (e.syllableOffset ?? 0) === so)
    );
    onAlignmentChange([...without, entry]);
  }, [alignment, onAlignmentChange, displayedToRaw, inferTime, canonicalRowByLine]);

  // Delete a chord (real or virtual). For virtual carry-forwards, adds a
  // suppression sentinel so enrichedChordMap does not re-add the carry-forward.
  const deleteChord = useCallback((li, wi, so) => {
    if (!onAlignmentChange) return;
    // Remove any existing entry at this position (real chord or old suppression)
    const filtered = (alignment || []).filter(
      e => !(e.lineIndex === li && e.wordIndex === wi && (e.syllableOffset ?? 0) === so)
    );
    // Add a suppression sentinel so enrichedChordMap won't carry-forward here
    filtered.push({ chord: null, lineIndex: li, wordIndex: wi, syllableOffset: so, suppressed: true });
    onAlignmentChange(filtered);
  }, [alignment, onAlignmentChange]);

  // Track last right-click per position for double-right-click detection
  const lastRightClickRef = useRef({ key: null, time: 0 });

  // ── Drag handlers ────────────────────────────────────────────────────────
  const handleDragStart = useCallback((e, li, wi, so, displayedChord) => {
    const sourceEntry = (alignment || []).find(
      (entry) => entry?.chord && Number(entry.lineIndex) === Number(li) && Number(entry.wordIndex) === Number(wi) && Number(entry.syllableOffset || 0) === Number(so || 0)
    );
    const rawChord = sourceEntry?.chord ?? realChordMap[li]?.[posKey(wi, so)];
    const source = {
      lineIndex: li,
      wordIndex: wi,
      syllableOffset: so,
      rawChord,
      displayedChord,
      sourceExists: !!sourceEntry,
    };
    setDragSource(source);
    setDragActive(true);
    dragTxnIdRef.current += 1;
    committedTxnRef.current = 0;
    dragSourceRef.current = source;
    lastDragSourceRef.current = source;
    dropInFlightRef.current = false;
    dropTargetRef.current = null;
    setHoveredChord(null);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', displayedChord);
    // Safari interoperability: also populate legacy text flavor.
    try { e.dataTransfer.setData('text', displayedChord); } catch { /* ignore */ }
    try {
      e.dataTransfer.setData('application/x-chord-source', JSON.stringify(source));
    } catch {
      // ignore
    }
  }, [realChordMap, alignment]);

  const primePreDragSource = useCallback((li, wi, so, displayedChord) => {
    const sourceEntry = (alignment || []).find(
      (entry) => entry?.chord && Number(entry.lineIndex) === Number(li) && Number(entry.wordIndex) === Number(wi) && Number(entry.syllableOffset || 0) === Number(so || 0)
    );
    const rawChord = sourceEntry?.chord ?? realChordMap[li]?.[posKey(wi, so)];
    preDragSourceRef.current = {
      lineIndex: li,
      wordIndex: wi,
      syllableOffset: so,
      rawChord,
      displayedChord,
      sourceExists: !!sourceEntry,
    };
  }, [alignment, realChordMap]);

  const handleDragEnd = useCallback(() => {
    setDropTarget(null);
    // Some browsers (notably Safari) may fire dragend before drop.
    // Keep source alive briefly so the drop handler can still resolve it.
    setTimeout(() => {
      if (dropInFlightRef.current) {
        dropInFlightRef.current = false;
        return;
      }
      setDragSource(null);
      setDragActive(false);
      dragSourceRef.current = null;
      preDragSourceRef.current = null;
    }, 150);
  }, []);

  const handleDragOver = useCallback((e, li, wi, so, anchorProgress = null, gapProgress = null, slotKey = null, slotKind = 'word', slotMeta = null, routeContext = 'lyric') => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const next = makeDropPayload({
      lineIndex: li,
      wordIndex: wi,
      syllableOffset: so,
      slotKind,
      anchorProgress,
      gapProgress,
      slotId: slotKey,
      slotMeta,
      routeContext,
    });
    dropTargetRef.current = next;
    setDropTarget(next);
  }, [makeDropPayload]);

  const handleDragLeave = useCallback((e) => {
    if (dragActive) return;
    if (!e.currentTarget.contains(e.relatedTarget)) {
      dropTargetRef.current = null;
      setDropTarget(null);
    }
  }, [dragActive]);

  const handleDrop = useCallback((e, targetLi, targetWi, targetSo = 0, targetAnchorProgress = null, targetGapProgress = null, slotKind = 'word', slotPayload = null, dropRoute = 'lyric') => {
    if (dropInFlightRef.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    dropInFlightRef.current = true;
    const activeDropTarget = slotPayload || dropTargetRef.current || dropTarget;
    setDropTarget(null);
    dropTargetRef.current = null;
    let activeDragSource = dragSource || dragSourceRef.current || lastDragSourceRef.current || preDragSourceRef.current;
    if (!activeDragSource) {
      try {
        const raw = e.dataTransfer?.getData('application/x-chord-source');
        if (raw) activeDragSource = JSON.parse(raw);
        if (!activeDragSource) {
          const txt = e.dataTransfer?.getData('text/plain') || e.dataTransfer?.getData('text');
          if (txt) {
            activeDragSource = {
              lineIndex: -999,
              wordIndex: 0,
              syllableOffset: 0,
              rawChord: displayedToRaw(txt),
              displayedChord: txt,
              sourceExists: false,
            };
          }
        }
      } catch {
        // ignore
      }
    }
    if (!activeDragSource || !onAlignmentChange) {
      setDragActive(false);
      dropInFlightRef.current = false;
      return;
    }

    const { lineIndex: srcLi, wordIndex: srcWi, syllableOffset: srcSo = 0, rawChord, displayedChord, sourceExists } = activeDragSource;
    if (dropRoute === 'lyric' && !slotPayload && !activeDropTarget) {
      abortedLyricDropCountRef.current += 1;
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[ChordDrop:abort]', { reason: 'missing-lyric-slot-payload', abortedLyricDrops: abortedLyricDropCountRef.current });
      }
      setDragSource(null);
      setDragActive(false);
      dropInFlightRef.current = false;
      return;
    }
    if (activeDropTarget?.txnId != null && Number(activeDropTarget.txnId) !== Number(dragTxnIdRef.current)) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[ChordDrop:abort]', { reason: 'stale-slot-txn', slotTxn: activeDropTarget?.txnId, dragTxn: dragTxnIdRef.current });
      }
      setDragSource(null);
      setDragActive(false);
      dropInFlightRef.current = false;
      return;
    }

    const resolvedTargetLi = activeDropTarget?.lineIndex ?? targetLi;
    const resolvedTargetWi = activeDropTarget?.wordIndex ?? targetWi;
    const resolvedTargetSo = activeDropTarget?.syllableOffset ?? targetSo;
    const resolvedAnchorProgress = Number.isFinite(Number(activeDropTarget?.anchorProgress))
      ? Number(activeDropTarget.anchorProgress)
      : targetAnchorProgress;
    const resolvedGapProgress = Number.isFinite(Number(activeDropTarget?.gapProgress))
      ? Number(activeDropTarget.gapProgress)
      : targetGapProgress;
    const resolvedSlotKind = activeDropTarget?.slotKind || slotKind;
    const resolvedSlotMeta = activeDropTarget?.slotMeta || null;
    const hasSubslotMove = Number.isFinite(Number(resolvedAnchorProgress)) || Number.isFinite(Number(resolvedGapProgress));
    if (srcLi === resolvedTargetLi && srcWi === resolvedTargetWi && srcSo === resolvedTargetSo && !hasSubslotMove) {
      setDragSource(null);
      setDragActive(false);
      dropInFlightRef.current = false;
      return;
    }

    const wasReal = rawChord !== undefined;
    const filtered = (alignment || []).filter(e => {
      if (e.lineIndex === resolvedTargetLi && e.wordIndex === resolvedTargetWi && (e.syllableOffset ?? 0) === resolvedTargetSo) return false; // remove target (incl. suppressions)
      if (sourceExists && e.lineIndex === srcLi && e.wordIndex === srcWi && (e.syllableOffset ?? 0) === srcSo) return false;
      return true;
    });

    const raw = wasReal ? rawChord : displayedToRaw(displayedChord);
    // Always infer time based on TARGET position using the post-filter alignment,
    // so dragged chords get the correct playback timing for their new location.
    const lineTokens = canonicalRowByLine[resolvedTargetLi]?.tokens || null;
    const sourceTime = sourceExists
      ? (alignment || []).find((x) => x.lineIndex === srcLi && x.wordIndex === srcWi && (x.syllableOffset ?? 0) === srcSo)?.time
      : null;
    const directGapTime = (resolvedSlotKind === 'gap' && Number.isFinite(Number(resolvedSlotMeta?.resolvedGapTime)))
      ? Number(resolvedSlotMeta.resolvedGapTime)
      : null;
    const time = directGapTime != null
      ? (
          Number.isFinite(Number(sourceTime))
          && Math.abs(Number(sourceTime) - Number(directGapTime)) <= EXACT_MATCH_EPS_SEC
            ? Number(sourceTime)
            : Number(directGapTime)
        )
      : resolveDropTime({
      lineIndex: resolvedTargetLi,
      targetWordIndex: resolvedTargetWi,
      targetSyllableOffset: resolvedTargetSo,
      slotKind: resolvedSlotKind,
      anchorProgress: resolvedAnchorProgress,
      gapProgress: resolvedGapProgress,
      gapStartTime: Number.isFinite(Number(resolvedSlotMeta?.gapStartTime)) ? Number(resolvedSlotMeta.gapStartTime) : null,
      gapEndTime: Number.isFinite(Number(resolvedSlotMeta?.gapEndTime)) ? Number(resolvedSlotMeta.gapEndTime) : null,
      sourceTime,
      fromAlignment: filtered,
      lineTokens,
    });
    const safeAnchorProgress = Number.isFinite(Number(resolvedAnchorProgress))
      ? Math.max(0, Math.min(1, Number(resolvedAnchorProgress)))
      : null;
    const safeGapProgress = Number.isFinite(Number(resolvedGapProgress))
      ? Math.max(0, Math.min(1, Number(resolvedGapProgress)))
      : null;
    const timed = time;
    const newEntry = { chord: raw, lineIndex: resolvedTargetLi, wordIndex: resolvedTargetWi };
    if (resolvedTargetSo > 0) newEntry.syllableOffset = resolvedTargetSo;
    if (safeAnchorProgress != null) newEntry.anchorProgress = Number(safeAnchorProgress.toFixed(3));
    if (safeGapProgress != null) newEntry.gapProgress = Number(safeGapProgress.toFixed(3));
    if (resolvedSlotKind === 'edge-pre') newEntry.anchorRule = 'manual-slot-drop-pre-edge';
    else if (resolvedSlotKind === 'edge-post') newEntry.anchorRule = 'manual-slot-drop-post-edge';
    else newEntry.anchorRule = 'manual-slot-drop';
    if (timed != null) newEntry.time = Number(timed.toFixed(3));
    const nextAlignment = [...filtered];
    if (!sourceExists) {
      // Moving a carry-forward/virtual chord: suppress original rendered slot.
      nextAlignment.push({
        chord: null,
        lineIndex: srcLi,
        wordIndex: srcWi,
        syllableOffset: srcSo,
        suppressed: true,
      });
    }
    nextAlignment.push(newEntry);
    const txnId = dragTxnIdRef.current;
    if (committedTxnRef.current === txnId) {
      setDragSource(null);
      setDragActive(false);
      dragSourceRef.current = null;
      lastDragSourceRef.current = null;
      preDragSourceRef.current = null;
      dropInFlightRef.current = false;
      return;
    }
    committedTxnRef.current = txnId;
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.debug('[ChordDrop]', {
        txnId,
        source: { lineIndex: srcLi, wordIndex: srcWi, syllableOffset: srcSo, sourceExists },
        target: {
          lineIndex: resolvedTargetLi,
          wordIndex: resolvedTargetWi,
          syllableOffset: resolvedTargetSo,
          slotKind: resolvedSlotKind,
          slotId: activeDropTarget?.slotId ?? null,
          dropRoute,
          anchorProgress: safeAnchorProgress,
          gapProgress: safeGapProgress,
          slotMeta: resolvedSlotMeta,
        },
        resolvedTime: timed != null ? Number(timed.toFixed(3)) : null,
      });
    }
    onAlignmentChange(nextAlignment);
    setDragSource(null);
    setDragActive(false);
    dragSourceRef.current = null;
    lastDragSourceRef.current = null;
    preDragSourceRef.current = null;
    dropInFlightRef.current = false;
  }, [dragSource, dropTarget, alignment, onAlignmentChange, displayedToRaw, resolveDropTime, canonicalRowByLine]);

  const handleSheetDragOver = useCallback((e) => {
    if (!dragActive) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, [dragActive]);

  const handleSheetDrop = useCallback((e) => {
    if (!dragActive || !onAlignmentChange) return;
    const t = dropTargetRef.current || dropTarget;
    if (!t) return;
    if (t.routeContext !== 'section') {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[ChordDrop:sheet-skip]', { reason: 'non-section-target', routeContext: t.routeContext || null });
      }
      return;
    }
    handleDrop(
      e,
      t.lineIndex,
      t.wordIndex,
      t.syllableOffset ?? 0,
      t.anchorProgress ?? null,
      t.gapProgress ?? null,
      t.slotKind || 'word',
      t,
      'section-fallback',
    );
  }, [dragActive, onAlignmentChange, dropTarget, handleDrop]);

  const getTokenDropTarget = useCallback((tokens, idx) => {
    const tok = tokens?.[idx];
    if (!tok || tok.isWord) return null;

    const lastIdx = (tokens?.length || 1) - 1;
    // Canonical line-edge spaces: force edge slot semantics.
    if (tok.ownerWordIndex == null && idx === 0) {
      let firstWord = null;
      for (let i = 0; i < (tokens?.length || 0); i++) {
        if (tokens[i]?.isWord) { firstWord = Number(tokens[i].wordIndex); break; }
      }
      if (Number.isInteger(firstWord)) return { wordIndex: firstWord - 0.5, syllableOffset: 0, slotKind: 'edge-pre' };
    }
    if (tok.ownerWordIndex == null && idx === lastIdx) {
      let lastWord = null;
      for (let i = lastIdx; i >= 0; i--) {
        if (tokens[i]?.isWord) { lastWord = Number(tokens[i].wordIndex); break; }
      }
      if (Number.isInteger(lastWord)) return { wordIndex: lastWord + 0.5, syllableOffset: 0, slotKind: 'edge-post' };
    }

    // Split-word separator: target the owning word at the next subword anchor.
    if (tok.ownerWordIndex != null && Number.isInteger(tok.ownerWordIndex)) {
      const owner = Number(tok.ownerWordIndex);
      let leftSeg = null;
      for (let i = idx - 1; i >= 0; i--) {
        if (tokens[i]?.isWord && Number(tokens[i].wordIndex) === owner) {
          leftSeg = tokens[i];
          break;
        }
      }
      if (leftSeg) {
        return { wordIndex: owner, syllableOffset: Number(leftSeg.syllableOffset || 0) + 1, slotKind: 'subword' };
      }
      return { wordIndex: owner, syllableOffset: 0, slotKind: 'subword' };
    }

    let prevWord = null;
    let nextWord = null;
    for (let i = idx - 1; i >= 0; i--) {
      if (tokens[i].isWord) { prevWord = tokens[i].wordIndex; break; }
    }
    for (let i = idx + 1; i < tokens.length; i++) {
      if (tokens[i].isWord) { nextWord = tokens[i].wordIndex; break; }
    }
    if (prevWord != null && nextWord != null && prevWord !== nextWord) {
      return { wordIndex: prevWord + 0.5, syllableOffset: 0, slotKind: 'gap' };
    }
    if (prevWord != null) return { wordIndex: prevWord + 0.5, syllableOffset: 0, slotKind: 'edge-post' };
    if (nextWord != null) return { wordIndex: nextWord - 0.5, syllableOffset: 0, slotKind: 'edge-pre' };
    return null;
  }, []);

  const segmentWordForDrop = useCallback((word) => {
    return getWordDropSegments(word);
  }, []);

  const applyTimedWordEdit = useCallback((lineIndex, wordIndex, params = {}) => {
    if (!onTimedLyricsChange) return { ok: false, error: 'timing-edit-disabled' };
    const before = [...(effectiveTimedLyrics || [])].map((w) => ({ ...w }));
    if (!before.length) return { ok: false, error: 'no-timed-words' };
    if (params.captureUndo !== false) timingUndoSnapshotRef.current = before;

    const result = applyWordTimingEdit(before, {
      lineIndex,
      wordIndex,
      newStart: params.newStart,
      newEnd: params.newEnd,
      mode: params.mode || 'both',
      minDurationSec: params.minDurationSec || 0.02,
    });
    if (result.error || !Array.isArray(result.timedLyrics)) {
      return { ok: false, error: result.error || 'timing-edit-failed' };
    }
    onTimedLyricsChange(result.timedLyrics);
    return { ok: true, diagnostics: result.diagnostics };
  }, [effectiveTimedLyrics, onTimedLyricsChange]);

  const handleTimingUndo = useCallback(() => {
    if (!onTimedLyricsChange || !timingUndoSnapshotRef.current) return;
    onTimedLyricsChange(timingUndoSnapshotRef.current);
    timingUndoSnapshotRef.current = null;
  }, [onTimedLyricsChange]);

  useEffect(() => {
    if (!dragTimingHandle) return undefined;
    const onMove = (evt) => {
      const lineEl = lineRefs.current[dragTimingHandle.lineIndex];
      if (!lineEl) return;
      const timeline = lineEl.querySelector('.chord-sheet__timing-row--interactive');
      if (!timeline) return;
      const rect = timeline.getBoundingClientRect();
      const x = Math.max(rect.left, Math.min(rect.right, evt.clientX));
      const p = (x - rect.left) / Math.max(1, rect.width);
      const t = dragTimingHandle.min + p * (dragTimingHandle.max - dragTimingHandle.min);
      if (dragTimingHandle.handle === 'start') {
        applyTimedWordEdit(dragTimingHandle.lineIndex, dragTimingHandle.wordIndex, { mode: 'start', newStart: t, captureUndo: false });
      } else {
        applyTimedWordEdit(dragTimingHandle.lineIndex, dragTimingHandle.wordIndex, { mode: 'end', newEnd: t, captureUndo: false });
      }
    };
    const onUp = () => setDragTimingHandle(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragTimingHandle, applyTimedWordEdit]);

  // ── Intro / outro ────────────────────────────────────────────────────────
  const introChords = useMemo(() => {
    const entries = (displayAlignment || [])
      .filter((e) => e && e.chord && Number(e.lineIndex) === -1)
      .sort((a, b) => {
        const ta = Number.isFinite(Number(a.time)) ? Number(a.time) : Number.POSITIVE_INFINITY;
        const tb = Number.isFinite(Number(b.time)) ? Number(b.time) : Number.POSITIVE_INFINITY;
        if (ta !== tb) return ta - tb;
        return Number(a.wordIndex || 0) - Number(b.wordIndex || 0);
      });
    if (!entries.length) return null;
    const byWord = new Map();
    entries.forEach((e) => {
      const wi = Number(e.wordIndex || 0);
      if (!byWord.has(wi)) byWord.set(wi, e.chord);
    });
    return [...byWord.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([wordIndex, chord]) => ({ wordIndex, chord }));
  }, [displayAlignment]);

  const outroChords = useMemo(() => {
    const entries = (displayAlignment || [])
      .filter((e) => e && e.chord && Number(e.lineIndex) === -2)
      .sort((a, b) => {
        const ta = Number.isFinite(Number(a.time)) ? Number(a.time) : Number.POSITIVE_INFINITY;
        const tb = Number.isFinite(Number(b.time)) ? Number(b.time) : Number.POSITIVE_INFINITY;
        if (ta !== tb) return ta - tb;
        return Number(a.wordIndex || 0) - Number(b.wordIndex || 0);
      });
    if (!entries.length) return null;
    const byWord = new Map();
    entries.forEach((e) => {
      const wi = Number(e.wordIndex || 0);
      if (!byWord.has(wi)) byWord.set(wi, e.chord);
    });
    return [...byWord.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([wordIndex, chord]) => ({ wordIndex, chord }));
  }, [displayAlignment]);

  // Instrumental section chords: from explicit section-line alignment entries.
  const instrumentalChordsMap = useMemo(() => {
    const map = {};
    lines.forEach((line, li) => {
      if (!/^\[(instrumental|solo|guitar\s+solo)\b/i.test(line.trim())) return;
      const entries = (displayAlignment || [])
        .filter((e) => e && e.chord && Number(e.lineIndex) === li)
        .sort((a, b) => {
          const ta = Number.isFinite(Number(a.time)) ? Number(a.time) : Number.POSITIVE_INFINITY;
          const tb = Number.isFinite(Number(b.time)) ? Number(b.time) : Number.POSITIVE_INFINITY;
          if (ta !== tb) return ta - tb;
          return Number(a.wordIndex || 0) - Number(b.wordIndex || 0);
        });
      if (!entries.length) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.debug('[SectionRender]', { lineIndex: li, section: 'instrumental', instrumentalSourceCount: 0, instrumentalRenderedCount: 0 });
        }
        return;
      }
      const byWord = new Map();
      entries.forEach((e) => {
        const wi = Number(e.wordIndex || 0);
        if (!byWord.has(wi)) byWord.set(wi, e.chord);
      });
      map[li] = [...byWord.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([wordIndex, chord]) => ({ wordIndex, chord }));
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[SectionRender]', {
          lineIndex: li,
          section: 'instrumental',
          instrumentalSourceCount: entries.length,
          instrumentalRenderedCount: map[li].length,
        });
      }
    });
    return map;
  }, [displayAlignment, lines]);

  // Maps section header line index → first lyric line index in that section.
  // Used for double-click-to-add-chord on section headers with no chords.
  const sectionFirstLyricLine = useMemo(() => {
    const map = {};
    for (let li = 0; li < lines.length; li++) {
      if (!/^\[.+\]/.test(lines[li].trim())) continue;
      for (let j = li + 1; j < lines.length; j++) {
        const t = lines[j].trim();
        if (/^\[.+\]/.test(t)) break; // next section
        if (t) { map[li] = j; break; }
      }
    }
    return map;
  }, [lines]);

  // Returns true if a section (starting at header line li) has any chords assigned.
  const sectionHasChords = useCallback((li) => {
    const isIntro = /^\[intro\]/i.test(lines[li]?.trim());
    const isOutro = /^\[outro\]/i.test(lines[li]?.trim());
    const isInstrumental = /^\[(instrumental|solo|guitar\s+solo)\b/i.test(lines[li]?.trim());
    if (isIntro) return !!introChords?.length;
    if (isOutro) {
      if (outroChords?.length) return true;
      // Also check lyric lines within the outro section
      const firstLyric = sectionFirstLyricLine[li];
      if (firstLyric == null) return false;
      let nextSection = lines.length;
      for (let j = li + 1; j < lines.length; j++) {
        if (/^\[.+\]/.test(lines[j].trim())) { nextSection = j; break; }
      }
      return (alignment || []).some(a => a.lineIndex >= firstLyric && a.lineIndex < nextSection && a.chord);
    }
    if (isInstrumental) return !!(instrumentalChordsMap[li]?.length);
    // Regular section: check if any alignment entry falls in its lyric lines
    const firstLyric = sectionFirstLyricLine[li];
    if (firstLyric == null) return false;
    let nextSection = lines.length;
    for (let j = li + 1; j < lines.length; j++) {
      if (/^\[.+\]/.test(lines[j].trim())) { nextSection = j; break; }
    }
    return (alignment || []).some(a => a.lineIndex >= firstLyric && a.lineIndex < nextSection && a.chord);
  }, [lines, alignment, introChords, outroChords, instrumentalChordsMap, sectionFirstLyricLine]);

  // Start chord editing when a section header is double-clicked (only if no chords).
  const handleSectionHeaderDoubleClick = useCallback((li) => {
    if (!onAlignmentChange) return;
    if (sectionHasChords(li)) return; // only activate when no chords exist
    const isIntro = /^\[intro\]/i.test(lines[li]?.trim());
    const isOutro = /^\[outro\]/i.test(lines[li]?.trim());
    const isInstrumental = /^\[(instrumental|solo|guitar\s+solo)\b/i.test(lines[li]?.trim());
    if (isIntro) {
      setEditTarget({ lineIndex: -1, wordIndex: 0, initialValue: '', isNew: true });
    } else if (isOutro) {
      setEditTarget({ lineIndex: -2, wordIndex: 0, initialValue: '', isNew: true });
    } else if (isInstrumental) {
      setEditTarget({ lineIndex: li, wordIndex: 0, initialValue: '', isNew: true });
    } else {
      const firstLyric = sectionFirstLyricLine[li];
      if (firstLyric != null) {
        setEditTarget({ lineIndex: firstLyric, wordIndex: 0, initialValue: '', isNew: true });
      }
    }
  }, [sectionHasChords, sectionFirstLyricLine, lines, onAlignmentChange]);


  // Renders a single orphaned-chord spacer cell in both the chord row and lyric row.
  const renderSpacerChordCell = (li, spEntry, keyPrefix) => {
    const { pk, wordIndex: swi } = spEntry;
    const so = Number(pk.split(':')[1] || 0);
    const chord = shownChordsForLine(li)[pk];
    if (!chord) return null;
    const isEditing = editTarget?.lineIndex === li && editTarget?.wordIndex === swi && (editTarget?.syllableOffset ?? 0) === so;
    const isActive = visualActiveKey === `${li}:${swi}:${so}`;
    const t = timeMap[li]?.[pk];
    return (
      <span
        key={`${keyPrefix}-chord`}
        className="chord-sheet__chord-cell chord-sheet__chord-cell--spacer"
        style={{ minWidth: 0, width: 0 }}
      >
        {isEditing ? (
          <input
            ref={editInputRef}
            className="chord-sheet__chord-input"
            defaultValue={editTarget.initialValue}
            {...editInputSizing(editTarget.initialValue)}
            onBlur={(e) => commitEdit(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitEdit(e.target.value); }
              if (e.key === 'Escape') { e.preventDefault(); setEditTarget(null); }
            }}
          />
        ) : (
          <strong
            className={[
              'chord-sheet__chord',
              'chord-sheet__chord--interactive',
              isActive ? 'chord-sheet__chord--active' : '',
            ].join(' ').trim()}
            onDoubleClick={(e) => { e.stopPropagation(); openEdit(li, swi, so, chord, false); }}
            onMouseEnter={(e) => {
              let beatInfo = null;
              if (t != null && rawAnalysis?.beats?.length) {
                const beats = rawAnalysis.beats;
                let nearest = beats[0], minDiff = Math.abs(beats[0].time - t);
                for (const b of beats) {
                  const d = Math.abs(b.time - t);
                  if (d < minDiff) { minDiff = d; nearest = b; }
                }
                beatInfo = { measure: nearest.measure, beat: nearest.beat };
              }
              setHoveredChord({ chord, rect: e.currentTarget.getBoundingClientRect(), time: t, beatInfo });
            }}
            onMouseLeave={() => setHoveredChord(null)}
            onContextMenu={(e) => {
              e.preventDefault();
              setHoveredChord(null);
              const positionKey = `${li}:${swi}:0`;
              const now = Date.now();
              const last = lastRightClickRef.current;
              if (last.key === positionKey && now - last.time < 400) {
                lastRightClickRef.current = { key: null, time: 0 };
                deleteChord(li, swi, so);
                return;
              }
              lastRightClickRef.current = { key: positionKey, time: now };
            }}
            title="Double-click to edit • Double-right-click to delete"
          >
            {chord}
          </strong>
        )}
      </span>
    );
  };

  // Helper: get shownChords for a line (avoids passing as argument where not needed)
  function shownChordsForLine(li) { return enrichedChordMap[li] || {}; }

  // ── Chord row renderer ───────────────────────────────────────────────────
  const renderCanonicalChordRow = (li, row) => {
    const events = (row?.alignedEvents || [])
      .filter((evt) => evt && evt.chord)
      .sort((a, b) => Number(a.col || 0) - Number(b.col || 0));
    if (!events.length && !(editTarget?.lineIndex === li)) return null;

    const rowWidth = Math.max(
      (row?.lyricRow || '').length,
      ...events.map((evt) => Number(evt.col || 0) + String(evt.chord || '').length),
    );
    const ghost = ' '.repeat(Math.max(0, rowWidth));
    const editOnLine = editTarget?.lineIndex === li;
    const editTargetCol = (() => {
      if (!editOnLine || !row) return null;
      if (Number.isFinite(Number(editTarget.anchorCol))) return Number(editTarget.anchorCol);
      const wi = Number(editTarget.wordIndex);
      const so = Number(editTarget.syllableOffset || 0);
      const token = (row.tokens || []).find((t) => t?.isWord && Number(t.wordIndex) === wi && Number(t.syllableOffset || 0) === so)
        || (row.tokens || []).find((t) => t?.isWord && Number(t.wordIndex) === wi);
      if (token) return Number(token.startCol || 0);
      const lyricLen = String(row.lyricRow || '').length;
      if (Number.isFinite(wi) && wi < 0) return 0;
      return lyricLen;
    })();

    return (
      <div className="chord-sheet__chord-row chord-sheet__chord-row--canonical-pos" aria-hidden="true">
        <span className="chord-sheet__row-ghost">{ghost}</span>
        {editOnLine && editTargetCol != null && (
          <span
            className="chord-sheet__chord-sheet-edit-anchor"
            style={{ '--col': Math.max(0, Number(editTargetCol || 0)) }}
          >
            <input
              ref={editInputRef}
              className="chord-sheet__chord-input"
              defaultValue={editTarget.initialValue}
              {...editInputSizing(editTarget.initialValue)}
              onBlur={(e) => commitEdit(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); commitEdit(e.target.value); }
                if (e.key === 'Escape') { e.preventDefault(); setEditTarget(null); }
              }}
            />
          </span>
        )}
        {events.map((evt, idx) => {
          const wi = Number(evt.wordIndex);
          const so = Number(evt.syllableOffset || 0);
          const pk = posKey(wi, so);
          const chord = String(evt.chord || '');
          const roman = showAnalysis ? romanMap.get(`${li}-${pk}`) : null;
          const isReal = realChordMap[li]?.[pk] !== undefined;
          const isBeingDragged = dragSource?.lineIndex === li && dragSource?.wordIndex === wi && (dragSource?.syllableOffset ?? 0) === so;
          const isActive = visualActiveKey === `${li}:${wi}:${so}`;
          return (
            <strong
              key={`${idx}-${pk}-${chord}`}
              className={[
                'chord-sheet__chord',
                'chord-sheet__chord--pos',
                isReal ? 'chord-sheet__chord--interactive' : 'chord-sheet__chord--virtual',
                isBeingDragged ? 'chord-sheet__chord--dragging' : '',
                isActive ? 'chord-sheet__chord--active' : '',
              ].join(' ').trim()}
              style={{ '--col': Math.max(0, Number(evt.col || 0)) }}
              draggable
              onMouseDown={() => primePreDragSource(li, wi, so, chord)}
              onDragStart={(e) => handleDragStart(e, li, wi, so, chord)}
              onDragEnd={handleDragEnd}
              onDoubleClick={(e) => {
                e.stopPropagation();
                openEdit(li, wi, so, chord, !isReal, { anchorCol: Number(evt.col || 0) });
              }}
              onMouseEnter={(e) => {
                const t = evt.time ?? timeMap[li]?.[pk];
                let beatInfo = null;
                if (t != null && rawAnalysis?.beats?.length) {
                  const beats = rawAnalysis.beats;
                  let nearest = beats[0];
                  let minDiff = Math.abs(beats[0].time - t);
                  for (const b of beats) {
                    const d = Math.abs(b.time - t);
                    if (d < minDiff) { minDiff = d; nearest = b; }
                  }
                  beatInfo = { measure: nearest.measure, beat: nearest.beat };
                }
                setHoveredChord({ chord, rect: e.currentTarget.getBoundingClientRect(), time: t, beatInfo });
              }}
              onMouseLeave={() => setHoveredChord(null)}
              onContextMenu={(e) => {
                e.preventDefault();
                setHoveredChord(null);
                const positionKey = `${li}:${wi}:${so}`;
                const now = Date.now();
                const last = lastRightClickRef.current;
                if (last.key === positionKey && now - last.time < 400) {
                  lastRightClickRef.current = { key: null, time: 0 };
                  deleteChord(li, wi, so);
                  return;
                }
                lastRightClickRef.current = { key: positionKey, time: now };
              }}
              title={isReal ? 'Drag to move • Double-click to edit • Double-right-click to delete' : 'Double-click to pin • Double-right-click to remove'}
            >
              {chord}
              {roman && <sub className="chord-sheet__roman">{roman}</sub>}
            </strong>
          );
        })}
      </div>
    );
  };

  const renderEditableChordRow = (tokens, shownChords, li) => {
    const lineSpacers = spacerMap[li]?.sorted || [];
    let spacerIdx = 0;
    const cells = [];

    const flushSpacersBefore = (maxWordIndex) => {
      while (spacerIdx < lineSpacers.length && lineSpacers[spacerIdx].wordIndex < maxWordIndex) {
        const cell = renderSpacerChordCell(li, lineSpacers[spacerIdx], `spacer-${spacerIdx}`);
        if (cell) cells.push(cell);
        spacerIdx++;
      }
    };

    // Spacers with wordIndex < 0 go before all words
    flushSpacersBefore(0);

    tokens.forEach((tok, ti) => {
      if (!tok.isWord) {
        const gapTarget = getTokenDropTarget(tokens, ti);
        const spacerText = ' '.repeat(tok.text.length);
        cells.push(
          <span
            key={ti}
            className={`chord-sheet__space${gapTarget != null ? ' chord-sheet__gap-drop' : ''}${gapTarget != null && dropTarget?.lineIndex === li && dropTarget?.wordIndex === gapTarget.wordIndex && (dropTarget?.syllableOffset ?? 0) === (gapTarget.syllableOffset ?? 0) ? ' chord-sheet__gap-drop--active' : ''}`}
            onDragOver={gapTarget != null ? (e) => handleDragOver(
              e,
              li,
              gapTarget.wordIndex,
              gapTarget.syllableOffset ?? 0,
              null,
              null,
              null,
              gapTarget.slotKind || 'gap',
              (() => {
                if (gapTarget.slotKind !== 'gap') return null;
                const b = getGapBounds(li, gapTarget.wordIndex);
                if (!b) return null;
                return {
                  ...b,
                  resolvedGapTime: Number((Number(b.gapStartTime) + 0.5 * (Number(b.gapEndTime) - Number(b.gapStartTime))).toFixed(3)),
                };
              })(),
            ) : undefined}
            onDragEnter={gapTarget != null ? (e) => handleDragOver(
              e,
              li,
              gapTarget.wordIndex,
              gapTarget.syllableOffset ?? 0,
              null,
              null,
              null,
              gapTarget.slotKind || 'gap',
              (() => {
                if (gapTarget.slotKind !== 'gap') return null;
                const b = getGapBounds(li, gapTarget.wordIndex);
                if (!b) return null;
                return {
                  ...b,
                  resolvedGapTime: Number((Number(b.gapStartTime) + 0.5 * (Number(b.gapEndTime) - Number(b.gapStartTime))).toFixed(3)),
                };
              })(),
            ) : undefined}
            onDragLeave={gapTarget != null ? handleDragLeave : undefined}
            onDrop={gapTarget != null ? (e) => {
              const b = gapTarget.slotKind === 'gap' ? getGapBounds(li, gapTarget.wordIndex) : null;
              const slotPayload = makeDropPayload({
                lineIndex: li,
                wordIndex: gapTarget.wordIndex,
                syllableOffset: gapTarget.syllableOffset ?? 0,
                slotKind: gapTarget.slotKind || 'gap',
                anchorProgress: null,
                gapProgress: 0.5,
                slotId: `legacy-gap-${li}-${ti}`,
                slotMeta: b ? {
                  ...b,
                  resolvedGapTime: Number((Number(b.gapStartTime) + 0.5 * (Number(b.gapEndTime) - Number(b.gapStartTime))).toFixed(3)),
                } : null,
                routeContext: 'lyric',
              });
              handleDrop(e, li, gapTarget.wordIndex, gapTarget.syllableOffset ?? 0, null, 0.5, gapTarget.slotKind || 'gap', slotPayload, 'lyric');
            } : undefined}
            title={gapTarget != null ? 'Drop chord at this timing slot' : undefined}
            aria-hidden="true"
          >
            {spacerText}
          </span>
        );
        return;
      }

      // Spacers between the previous word and this one
      flushSpacersBefore(tok.wordIndex);

      const wi = tok.wordIndex;
      const so = tok.syllableOffset ?? 0;
      const pk = posKey(wi, so);
      const chord = shownChords[pk];
      const isEditing = editTarget?.lineIndex === li && editTarget?.wordIndex === wi && (editTarget?.syllableOffset ?? 0) === so;
      const isBeingDragged = dragSource?.lineIndex === li && dragSource?.wordIndex === wi && (dragSource?.syllableOffset ?? 0) === so;
      const isDropZone = dropTarget?.lineIndex === li && dropTarget?.wordIndex === wi && (dropTarget?.syllableOffset ?? 0) === so;
      const isReal = realChordMap[li]?.[pk] !== undefined;

      cells.push(
        <span
          key={ti}
          className={[
            'chord-sheet__chord-cell',
            isDropZone ? 'chord-sheet__chord-cell--drop-target' : '',
          ].join(' ').trim()}
          style={{ minWidth: `${tok.text.length}ch` }}
          onDragOver={(e) => handleDragOver(e, li, wi, so, null, null, null, 'word')}
          onDragEnter={(e) => handleDragOver(e, li, wi, so, null, null, null, 'word')}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, li, wi, so, null, null, 'word', makeDropPayload({
            lineIndex: li,
            wordIndex: wi,
            syllableOffset: so,
            slotKind: 'word',
            slotId: `legacy-word-${li}-${wi}-${so}`,
            routeContext: 'lyric',
          }), 'lyric')}
        >
          {isEditing ? (
            <input
              ref={editInputRef}
              className="chord-sheet__chord-input"
              defaultValue={editTarget.initialValue}
              {...editInputSizing(editTarget.initialValue)}
              onBlur={(e) => commitEdit(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); commitEdit(e.target.value); }
                if (e.key === 'Escape') { e.preventDefault(); setEditTarget(null); }
              }}
            />
          ) : chord ? (
            <strong
              className={[
                'chord-sheet__chord',
                isReal ? 'chord-sheet__chord--interactive' : 'chord-sheet__chord--virtual',
                isBeingDragged ? 'chord-sheet__chord--dragging' : '',
                visualActiveKey === `${li}:${wi}:${so}` ? 'chord-sheet__chord--active' : '',
              ].join(' ').trim()}
              draggable
              onMouseDown={() => primePreDragSource(li, wi, so, chord)}
              onDragStart={(e) => handleDragStart(e, li, wi, so, chord)}
              onDragEnd={handleDragEnd}
              onDoubleClick={(e) => {
                e.stopPropagation();
                if (!isReal) {
                  pinCarryForward(li, wi, so, chord);
                } else {
                  openEdit(li, wi, so, chord, false);
                }
              }}
              onMouseEnter={(e) => {
                  const t = timeMap[li]?.[pk];
                  let beatInfo = null;
                  if (t != null && rawAnalysis?.beats?.length) {
                    const beats = rawAnalysis.beats;
                    let nearest = beats[0];
                    let minDiff = Math.abs(beats[0].time - t);
                    for (const b of beats) {
                      const d = Math.abs(b.time - t);
                      if (d < minDiff) { minDiff = d; nearest = b; }
                    }
                    beatInfo = { measure: nearest.measure, beat: nearest.beat };
                  }
                  setHoveredChord({ chord, rect: e.currentTarget.getBoundingClientRect(), time: t, beatInfo });
                }}
              onMouseLeave={() => setHoveredChord(null)}
              onContextMenu={(e) => {
                e.preventDefault();
                setHoveredChord(null);
                const positionKey = `${li}:${wi}:${so}`;
                const now = Date.now();
                const last = lastRightClickRef.current;
                if (last.key === positionKey && now - last.time < 400) {
                  lastRightClickRef.current = { key: null, time: 0 };
                  deleteChord(li, wi, so);
                  return;
                }
                lastRightClickRef.current = { key: positionKey, time: now };
                const rawChord = realChordMap[li]?.[pk];
                const alts = rawChord ? (alternativesMap[rawChord] || []) : [];
                if (alts.length) {
                  setAltMenu({ lineIndex: li, wordIndex: wi, syllableOffset: so, rawChord, alts, x: e.clientX, y: e.clientY });
                }
              }}
              title={isReal ? 'Drag to move • Double-click to edit • Double-right-click to delete' : 'Double-click to pin • Double-right-click to remove'}
            >
              {chord}
              {showAnalysis && romanMap.get(`${li}-${pk}`) && (
                <sub className="chord-sheet__roman">{romanMap.get(`${li}-${pk}`)}</sub>
              )}
            </strong>
          ) : null}
          <span className="chord-sheet__chord-spacer">{chord && (chord.length + 1) > tok.text.length ? chord + ' ' : tok.text}</span>
        </span>
      );
    });

    // Spacers after last word
    flushSpacersBefore(Infinity);

    return cells;
  };

  // ── Helpers ──────────────────────────────────────────────────────────────
  function chunkChords(arr, n = 4) {
    const rows = [];
    for (let i = 0; i < arr.length; i += n) rows.push(arr.slice(i, i + n));
    return rows;
  }

  function renderStandaloneChord(entry, lineIdx) {
    const { wordIndex: wi, chord } = entry;
    const roman = showAnalysis ? romanMap.get(`${lineIdx}-${posKey(wi, 0)}`) : null;
    const isActive = visualActiveKey === `${lineIdx}:${wi}:0`;
    const isEditing = editTarget?.lineIndex === lineIdx && editTarget?.wordIndex === wi;
    const t = timeMap[lineIdx]?.[posKey(wi, 0)];
    const handleMouseEnter = (e) => {
      let beatInfo = null;
      if (t != null && rawAnalysis?.beats?.length) {
        const beats = rawAnalysis.beats;
        let nearest = beats[0], minDiff = Math.abs(beats[0].time - t);
        for (const b of beats) {
          const d = Math.abs(b.time - t);
          if (d < minDiff) { minDiff = d; nearest = b; }
        }
        beatInfo = { measure: nearest.measure, beat: nearest.beat };
      }
      setHoveredChord({ chord, rect: e.currentTarget.getBoundingClientRect(), time: t, beatInfo });
    };
    const positionKey = `${lineIdx}:${wi}:0`;
    if (isEditing) {
      return (
        <input
          key={`edit-${wi}`}
          ref={editInputRef}
          className="chord-sheet__chord-input chord-sheet__chord-input--standalone"
          defaultValue={editTarget.initialValue}
          {...editInputSizing(editTarget.initialValue)}
          placeholder="A,E…"
          onBlur={(e) => commitEdit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commitEdit(e.target.value); }
            if (e.key === 'Escape') { e.preventDefault(); setEditTarget(null); }
          }}
        />
      );
    }
    return (
      <span
        key={wi}
        className={[
          'chord-sheet__intro-chord-label',
          'chord-sheet__chord--interactive',
          isActive ? 'chord-sheet__chord--active' : '',
        ].join(' ').trim()}
        draggable={!!onAlignmentChange}
        onMouseDown={() => primePreDragSource(lineIdx, wi, 0, chord)}
        onDragStart={(e) => handleDragStart(e, lineIdx, wi, 0, chord)}
        onDragEnd={handleDragEnd}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setHoveredChord(null)}
        onDoubleClick={(e) => {
          e.stopPropagation();
          if (onAlignmentChange) openEdit(lineIdx, wi, 0, chord, false);
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          setHoveredChord(null);
          if (!onAlignmentChange) return;
          const now = Date.now();
          const last = lastRightClickRef.current;
          if (last.key === positionKey && now - last.time < 400) {
            lastRightClickRef.current = { key: null, time: 0 };
            onAlignmentChange((alignment || []).filter(
              e => !(e.lineIndex === lineIdx && e.wordIndex === wi)
            ));
            return;
          }
          lastRightClickRef.current = { key: positionKey, time: now };
        }}
        title="Double-click to edit • Double-right-click to delete"
      >
        {chord}
        {roman && <sub className="chord-sheet__roman">{roman}</sub>}
      </span>
    );
  }

  function renderChordSection(label, chords, lineIdx) {
    if (!chords) return null;
    return (
      <>
        <div className="chord-sheet__section">{label}</div>
        {chunkChords(chords).map((row, ri) => (
          <div
            key={ri}
            className="chord-sheet__chord-row chord-sheet__chord-row--intro"
            onDragOver={onAlignmentChange ? (e) => handleDragOver(e, lineIdx, (row[row.length - 1]?.wordIndex ?? 0) + 0.5, 0, null, 1, null, 'edge-post', null, 'section') : undefined}
            onDrop={onAlignmentChange ? (e) => handleDrop(e, lineIdx, (row[row.length - 1]?.wordIndex ?? 0) + 0.5, 0, null, 1, 'edge-post', makeDropPayload({
              lineIndex: lineIdx,
              wordIndex: (row[row.length - 1]?.wordIndex ?? 0) + 0.5,
              syllableOffset: 0,
              slotKind: 'edge-post',
              gapProgress: 1,
              routeContext: 'section',
            }), 'section') : undefined}
          >
            {row.map(entry => renderStandaloneChord(entry, lineIdx))}
          </div>
        ))}
      </>
    );
  }

  function renderInlineChordRows(chords, lineIdx) {
    if (!chords || !chords.length) {
      const isEditingLine = editTarget?.lineIndex === lineIdx;
      return (
        <div
          className="chord-sheet__chord-row chord-sheet__chord-row--intro"
          onDragOver={onAlignmentChange ? (e) => handleDragOver(e, lineIdx, 0.5, 0, null, 1, null, 'edge-post', null, 'section') : undefined}
          onDrop={onAlignmentChange ? (e) => handleDrop(e, lineIdx, 0.5, 0, null, 1, 'edge-post', makeDropPayload({
            lineIndex: lineIdx,
            wordIndex: 0.5,
            syllableOffset: 0,
            slotKind: 'edge-post',
            gapProgress: 1,
            routeContext: 'section',
          }), 'section') : undefined}
        >
          {isEditingLine ? (
            <input
              ref={editInputRef}
              className="chord-sheet__chord-input chord-sheet__chord-input--standalone"
              defaultValue={editTarget.initialValue}
              {...editInputSizing(editTarget.initialValue)}
              placeholder="A,E…"
              onBlur={(e) => commitEdit(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); commitEdit(e.target.value); }
                if (e.key === 'Escape') { e.preventDefault(); setEditTarget(null); }
              }}
            />
          ) : (
            <span className="chord-sheet__space" aria-hidden="true">{' '}</span>
          )}
        </div>
      );
    }
    return chunkChords(chords).map((row, ri) => (
      <div
        key={ri}
        className="chord-sheet__chord-row chord-sheet__chord-row--intro"
        onDragOver={onAlignmentChange ? (e) => handleDragOver(e, lineIdx, (row[row.length - 1]?.wordIndex ?? 0) + 0.5, 0, null, 1, null, 'edge-post', null, 'section') : undefined}
        onDrop={onAlignmentChange ? (e) => handleDrop(e, lineIdx, (row[row.length - 1]?.wordIndex ?? 0) + 0.5, 0, null, 1, 'edge-post', makeDropPayload({
          lineIndex: lineIdx,
          wordIndex: (row[row.length - 1]?.wordIndex ?? 0) + 0.5,
          syllableOffset: 0,
          slotKind: 'edge-post',
          gapProgress: 1,
          routeContext: 'section',
        }), 'section') : undefined}
      >
        {row.map(entry => renderStandaloneChord(entry, lineIdx))}
      </div>
    ));
  }

  // ── Render ───────────────────────────────────────────────────────────────
  const applyTimingMenuEdit = useCallback(() => {
    if (!wordTimingMenu) return;
    const start = Number(wordTimingMenu.startDraft);
    const end = Number(wordTimingMenu.endDraft);
    if (!Number.isFinite(start) || !Number.isFinite(end)) {
      setWordTimingMenu((prev) => (prev ? { ...prev, error: 'Start and end must be numeric seconds.' } : prev));
      return;
    }
    if (start < 0 || end < 0) {
      setWordTimingMenu((prev) => (prev ? { ...prev, error: 'Times must be non-negative.' } : prev));
      return;
    }
    if (end < start) {
      setWordTimingMenu((prev) => (prev ? { ...prev, error: 'End must be greater than or equal to start.' } : prev));
      return;
    }
    const res = applyTimedWordEdit(wordTimingMenu.lineIndex, wordTimingMenu.wordIndex, {
      mode: 'both',
      newStart: start,
      newEnd: end,
    });
    if (!res.ok) {
      setWordTimingMenu((prev) => (prev ? { ...prev, error: `Update failed: ${res.error || 'unknown error'}` } : prev));
      return;
    }
    setWordTimingMenu(null);
  }, [wordTimingMenu, applyTimedWordEdit]);

  const formatSecs = (value) => (
    Number.isFinite(value) ? `${value.toFixed(2)}s` : 'n/a'
  );
  const showGapTargets = dragActive;
  const lyricsHasIntroSection = useMemo(() => {
    const first = lines.find(l => l.trim());
    return first ? /^\[intro\]/i.test(first.trim()) : false;
  }, [lines]);

  const lyricsHasOutroSection = useMemo(() => {
    return lines.some(l => /^\[outro\]/i.test(l.trim()));
  }, [lines]);

  // For each lyric line that is the last lyrics line inside an [Outro] section,
  // mark it so we can append trailing outroChords (lineIndex=-2) after it.
  const lastLyricInOutroByLine = useMemo(() => {
    const result = {};
    let inOutro = false;
    let lastOutroLyricLi = null;
    for (let i = 0; i < lines.length; i++) {
      const t = lines[i].trim();
      if (!t) continue;
      if (/^\[.+\]/.test(t)) {
        if (inOutro && lastOutroLyricLi !== null) {
          result[lastOutroLyricLi] = true;
          lastOutroLyricLi = null;
        }
        inOutro = /^\[outro\]/i.test(t);
      } else if (inOutro) {
        lastOutroLyricLi = i;
      }
    }
    if (inOutro && lastOutroLyricLi !== null) {
      result[lastOutroLyricLi] = true;
    }
    return result;
  }, [lines]);

  if (!lyrics) {
    return (
      <div className="chord-sheet-empty">
        <div className="chord-sheet-empty__icon">🎼</div>
        <p>Upload a file and add lyrics to get started</p>
      </div>
    );
  }

  return (
    <div
      className={`chord-sheet${showGapTargets ? ' chord-sheet--show-gaps' : ''}`}
      onDragOver={handleSheetDragOver}
      onDrop={handleSheetDrop}
    >
      {!lyricsHasIntroSection && renderChordSection('[Intro]', introChords, -1)}
      {lines.map((line, li) => {
        const canonicalRow = canonicalRowByLine[li];
        const isSection = /^\[.+\]/.test(line.trim());
        const isBlank = line.trim() === '';

        if (isBlank) return <div key={li} className="chord-sheet__spacer" />;

        if (isSection) {
          const isIntroLine = /^\[intro\]/i.test(line.trim());
          const isOutroLine = /^\[outro\]/i.test(line.trim());
          const isInstrumentalLine = /^\[(instrumental|solo|guitar\s+solo)\b/i.test(line.trim());
          // Only show outroChords at this [Outro] header if no lyric lines follow it
          // (before the next section). When [Outro] has words, those lines render chords inline.
          const outroHasFollowingLyrics = isOutroLine && (() => {
            for (let j = li + 1; j < lines.length; j++) {
              const t = lines[j].trim();
              if (!t) continue;
              if (/^\[.+\]/.test(t)) break;
              return true;
            }
            return false;
          })();
          const noChords = !sectionHasChords(li);
          return (
            <React.Fragment key={li}>
              <div
                className={`chord-sheet__section${noChords && onAlignmentChange ? ' chord-sheet__section--clickable' : ''}`}
                onDoubleClick={() => handleSectionHeaderDoubleClick(li)}
                onDragOver={onAlignmentChange && noChords ? (e) => handleDragOver(e, /^\[intro\]/i.test(line.trim()) ? -1 : (/^\[outro\]/i.test(line.trim()) ? -2 : li), 0, 0, null, null, null, 'word', null, 'section') : undefined}
                onDrop={onAlignmentChange && noChords ? (e) => handleDrop(e, /^\[intro\]/i.test(line.trim()) ? -1 : (/^\[outro\]/i.test(line.trim()) ? -2 : li), 0, 0, null, null, 'word', makeDropPayload({
                  lineIndex: /^\[intro\]/i.test(line.trim()) ? -1 : (/^\[outro\]/i.test(line.trim()) ? -2 : li),
                  wordIndex: 0,
                  syllableOffset: 0,
                  slotKind: 'word',
                  routeContext: 'section',
                }), 'section') : undefined}
                title={noChords && onAlignmentChange ? 'Double-click to add chords to this section' : undefined}
              >
                {line.trim()}
              </div>
              {isIntroLine && renderInlineChordRows(introChords || [], -1)}
              {isOutroLine && !outroHasFollowingLyrics && renderInlineChordRows(outroChords || [], -2)}
              {isInstrumentalLine && renderInlineChordRows(instrumentalChordsMap[li] || [], li)}
              {!isIntroLine && !isOutroLine && !isInstrumentalLine && canonicalRow?.type === 'section' && canonicalRow.headerChordRow && (
                <div className="chord-sheet__chord-row chord-sheet__chord-row--canonical" aria-hidden="true">
                  <span className="chord-sheet__row-text">{canonicalRow.headerChordRow}</span>
                </div>
              )}
            </React.Fragment>
          );
        }

        const shownChords = enrichedChordMap[li] || {};
        const hasChords = Object.keys(shownChords).length > 0;
        const isEditingThisLine = editTarget?.lineIndex === li;
        const lyricText = canonicalRow?.type === 'lyric' ? (canonicalRow.lyricRow || line) : line;
        const tokens = (canonicalRow?.type === 'lyric' && Array.isArray(canonicalRow.tokens) && canonicalRow.tokens.length)
          ? canonicalRow.tokens
          : tokenizeLyricLine(lyricText);
        const lineTimedWords = timedWordsByLine[li] || [];

        return (
          <React.Fragment key={li}>
          <div
            className={`chord-sheet__line${activeLineForFollow === li ? ' chord-sheet__line--active' : ''}`}
            ref={(el) => { lineRefs.current[li] = el; }}
          >
            {(hasChords || isEditingThisLine) && (
              canonicalRow?.type === 'lyric'
                ? renderCanonicalChordRow(li, canonicalRow)
                : (
                  <div className="chord-sheet__chord-row" aria-hidden="true">
                    {renderEditableChordRow(tokens, shownChords, li)}
                  </div>
                )
            )}
            <div className="chord-sheet__lyric-row">
              {(() => {
                const lyricCells = [];

                tokens.forEach((tok, ti) => {
                  const tokenWordIndex = tok.isWord ? tok.wordIndex : tok.ownerWordIndex;
                  const gapTarget = !tok.isWord ? getTokenDropTarget(tokens, ti) : null;
                  const timing = tokenWordIndex != null ? wordTimingLookup[`${li}:${tokenWordIndex}`] : null;
                  const isSpokenNow = highlightWordsDuringPlayback && tokenWordIndex != null && timing?.start != null && currentTime != null && (() => {
                    const start = Number(timing.start);
                    const endVal = Number(timing.end);
                    const end = Number.isFinite(endVal) ? Math.max(start, endVal) : start + 0.12;
                    return currentTime >= start && currentTime <= end;
                  })();
                  const isGroupedWordToken = tokenWordIndex != null && !tok.isWord;
                  const groupHoverActive = tokenWordIndex != null && hoverWordKey === `${li}:${tokenWordIndex}`;
                  const tokenContent = (!showGapTargets)
                    ? tok.text
                    : (() => {
                      const chars = Array.from(String(tok.text || ''));
                      if (!chars.length) return tok.text;

                      if (tok.isWord) {
                        const wordSegments = (tokens || [])
                          .filter((t) => t?.isWord && Number(t.wordIndex) === Number(tok.wordIndex))
                          .sort((a, b) => Number(a.syllableOffset || 0) - Number(b.syllableOffset || 0));
                        const totalWordChars = Math.max(1, wordSegments.reduce((sum, seg) => sum + String(seg.text || '').length, 0));
                        const subSegments = segmentWordForDrop(tok.text);
                        const charsBeforeToken = wordSegments
                          .filter((seg) => Number(seg.syllableOffset || 0) < Number(tok.syllableOffset || 0))
                          .reduce((sum, seg) => sum + String(seg.text || '').length, 0);
                        const denom = Math.max(1, totalWordChars - 1);
                        let consumed = 0;
                        return subSegments.map((seg, gi) => {
                          const startPos = charsBeforeToken + consumed;
                          consumed += String(seg || '').length;
                          const tAnchorProgress = Math.max(0, Math.min(1, startPos / denom));
                          const slotKey = `l${li}-t${ti}-g${gi}`;
                          const isActiveSlot = dropTarget?.slotKey === slotKey;
                          return (
                            <span
                              key={slotKey}
                              className={`chord-sheet__drop-char${isActiveSlot ? ' chord-sheet__drop-char--active' : ''}`}
                              onDragOver={(e) => handleDragOver(e, li, tok.wordIndex, tok.syllableOffset ?? 0, tAnchorProgress, null, slotKey, 'subword')}
                              onDragEnter={(e) => handleDragOver(e, li, tok.wordIndex, tok.syllableOffset ?? 0, tAnchorProgress, null, slotKey, 'subword')}
                              onDrop={(e) => handleDrop(e, li, tok.wordIndex, tok.syllableOffset ?? 0, tAnchorProgress, null, 'subword', makeDropPayload({
                                lineIndex: li,
                                wordIndex: tok.wordIndex,
                                syllableOffset: tok.syllableOffset ?? 0,
                                slotKind: 'subword',
                                anchorProgress: tAnchorProgress,
                                slotId: slotKey,
                                routeContext: 'lyric',
                              }), 'lyric')}
                              onDragLeave={handleDragLeave}
                            >
                              {seg}
                            </span>
                          );
                        });
                      }

                      return chars.map((ch, ci) => {
                        if (!gapTarget) return <span key={`l${li}-t${ti}-c${ci}`}>{ch === ' ' ? '\u00A0' : ch}</span>;
                        let tAnchorProgress = null;
                        let tGapProgress = null;
                        if (Number.isInteger(Number(gapTarget.wordIndex)) && Number.isFinite(Number(gapTarget.syllableOffset))) {
                          const owner = Number(gapTarget.wordIndex);
                          const ownerSegs = (tokens || [])
                            .filter((t) => t?.isWord && Number(t.wordIndex) === owner)
                            .sort((a, b) => Number(a.syllableOffset || 0) - Number(b.syllableOffset || 0));
                          const ownerChars = Math.max(1, ownerSegs.reduce((sum, seg) => sum + String(seg.text || '').length, 0));
                          const leftSegsChars = ownerSegs
                            .filter((seg) => Number(seg.syllableOffset || 0) < Number(gapTarget.syllableOffset || 0))
                            .reduce((sum, seg) => sum + String(seg.text || '').length, 0);
                          const fracInside = ci / Math.max(1, chars.length);
                          const denom = Math.max(1, ownerChars - 1);
                          tAnchorProgress = Math.max(0, Math.min(1, (leftSegsChars + fracInside) / denom));
                        } else {
                          tGapProgress = (ci + 0.5) / Math.max(1, chars.length);
                        }
                        const slotKey = `l${li}-t${ti}-c${ci}`;
                        const isActiveSlot = dropTarget?.slotKey === slotKey;
                        return (
                          <span
                            key={slotKey}
                            className={`chord-sheet__drop-char${isActiveSlot ? ' chord-sheet__drop-char--active' : ''}`}
                            onDragOver={(e) => handleDragOver(
                              e,
                              li,
                              gapTarget.wordIndex,
                              gapTarget.syllableOffset ?? 0,
                              tAnchorProgress,
                              tGapProgress,
                              slotKey,
                              gapTarget.slotKind || 'gap',
                              (() => {
                                if (gapTarget.slotKind !== 'gap') return null;
                                const b = getGapBounds(li, gapTarget.wordIndex);
                                if (!b) return null;
                                const gp = Number.isFinite(Number(tGapProgress))
                                  ? Math.max(0, Math.min(1, Number(tGapProgress)))
                                  : 0.5;
                                return {
                                  ...b,
                                  resolvedGapTime: Number((Number(b.gapStartTime) + gp * (Number(b.gapEndTime) - Number(b.gapStartTime))).toFixed(3)),
                                };
                              })(),
                            )}
                            onDragEnter={(e) => handleDragOver(
                              e,
                              li,
                              gapTarget.wordIndex,
                              gapTarget.syllableOffset ?? 0,
                              tAnchorProgress,
                              tGapProgress,
                              slotKey,
                              gapTarget.slotKind || 'gap',
                              (() => {
                                if (gapTarget.slotKind !== 'gap') return null;
                                const b = getGapBounds(li, gapTarget.wordIndex);
                                if (!b) return null;
                                const gp = Number.isFinite(Number(tGapProgress))
                                  ? Math.max(0, Math.min(1, Number(tGapProgress)))
                                  : 0.5;
                                return {
                                  ...b,
                                  resolvedGapTime: Number((Number(b.gapStartTime) + gp * (Number(b.gapEndTime) - Number(b.gapStartTime))).toFixed(3)),
                                };
                              })(),
                            )}
                            onDrop={(e) => handleDrop(e, li, gapTarget.wordIndex, gapTarget.syllableOffset ?? 0, tAnchorProgress, tGapProgress, gapTarget.slotKind || 'gap', makeDropPayload({
                              lineIndex: li,
                              wordIndex: gapTarget.wordIndex,
                              syllableOffset: gapTarget.syllableOffset ?? 0,
                              slotKind: gapTarget.slotKind || 'gap',
                              anchorProgress: tAnchorProgress,
                              gapProgress: tGapProgress,
                              slotId: slotKey,
                              slotMeta: (() => {
                                if (gapTarget.slotKind !== 'gap') return null;
                                const b = getGapBounds(li, gapTarget.wordIndex);
                                if (!b) return null;
                                const gp = Number.isFinite(Number(tGapProgress))
                                  ? Math.max(0, Math.min(1, Number(tGapProgress)))
                                  : 0.5;
                                return {
                                  ...b,
                                  resolvedGapTime: Number((Number(b.gapStartTime) + gp * (Number(b.gapEndTime) - Number(b.gapStartTime))).toFixed(3)),
                                };
                              })(),
                              routeContext: 'lyric',
                            }), 'lyric')}
                            onDragLeave={handleDragLeave}
                          >
                            {ch === ' ' ? '\u00A0' : ch}
                          </span>
                        );
                      });
                    })();

                  lyricCells.push(
                    <span
                      key={ti}
                      className={(tok.isWord || isGroupedWordToken)
                        ? `chord-sheet__word${onAlignmentChange ? ' chord-sheet__word--interactive' : ''}${isSpokenNow ? ' chord-sheet__word--active' : ''}${groupHoverActive ? ' chord-sheet__word--group-hover' : ''}${isGroupedWordToken ? ' chord-sheet__word--split-sep' : ''}${timingEditMode && tokenWordIndex != null && selectedTimingWord?.lineIndex === li && Number(selectedTimingWord?.wordIndex) === Number(tokenWordIndex) ? ' chord-sheet__word--timing-selected' : ''}`
                        : 'chord-sheet__space'}
                      onMouseEnter={(tok.isWord || isGroupedWordToken) && tokenWordIndex != null
                        ? () => setHoverWordKey(`${li}:${tokenWordIndex}`)
                        : undefined}
                      onMouseLeave={(tok.isWord || isGroupedWordToken) && tokenWordIndex != null
                        ? () => setHoverWordKey((prev) => (prev === `${li}:${tokenWordIndex}` ? null : prev))
                        : undefined}
                      onDoubleClick={tok.isWord && onAlignmentChange ? (e) => {
                        e.stopPropagation();
                        const so = tok.syllableOffset ?? 0;
                        const shownChord = shownChords[posKey(tok.wordIndex, so)];
                        openEdit(li, tok.wordIndex, so, shownChord || '', !shownChord, {
                          anchorCol: Number(tok.startCol || 0),
                        });
                      } : undefined}
                      onClick={timingEditMode && tokenWordIndex != null ? () => {
                        setSelectedTimingWord({ lineIndex: li, wordIndex: Number(tokenWordIndex) });
                      } : undefined}
                      onDragOver={tok.isWord && !showGapTargets
                        ? (e) => handleDragOver(e, li, tok.wordIndex, tok.syllableOffset ?? 0, null, null, null, 'word')
                        : undefined}
                      onDragEnter={tok.isWord && !showGapTargets
                        ? (e) => handleDragOver(e, li, tok.wordIndex, tok.syllableOffset ?? 0, null, null, null, 'word')
                        : undefined}
                      onDragLeave={(tok.isWord || (!tok.isWord && gapTarget != null)) ? handleDragLeave : undefined}
                      onDrop={tok.isWord && !showGapTargets
                        ? (e) => handleDrop(e, li, tok.wordIndex, tok.syllableOffset ?? 0, null, null, 'word', makeDropPayload({
                          lineIndex: li,
                          wordIndex: tok.wordIndex,
                          syllableOffset: tok.syllableOffset ?? 0,
                          slotKind: 'word',
                          slotId: `tok-word-${li}-${ti}-${tok.wordIndex}-${tok.syllableOffset ?? 0}`,
                          routeContext: 'lyric',
                        }), 'lyric')
                        : undefined}
                      onContextMenu={(tok.isWord || isGroupedWordToken) ? (e) => {
                        const timingInfo = tokenWordIndex != null ? wordTimingLookup[`${li}:${tokenWordIndex}`] : null;
                        if (timingInfo?.start == null) return;
                        e.preventDefault();
                        e.stopPropagation();
                        const start = Number(timingInfo.start);
                        const end = Number.isFinite(Number(timingInfo.end)) ? Number(timingInfo.end) : start;
                        setWordTimingMenu({
                          x: e.clientX,
                          y: e.clientY,
                          lineIndex: li,
                          wordIndex: Number(tokenWordIndex),
                          word: timingInfo.word || tok.text,
                          start,
                          end,
                          startDraft: start.toFixed(3),
                          endDraft: end.toFixed(3),
                          error: '',
                          duration: Math.max(0, end - start),
                          matchedWhisperWord: timingInfo.matchedWhisperWord || null,
                        });
                        setSelectedTimingWord({ lineIndex: li, wordIndex: Number(tokenWordIndex) });
                      } : undefined}
                    >
                      {tokenContent}
                    </span>
                  );
                });
                return lyricCells;
              })()}
            </div>
            {timingEditMode && lineTimedWords.length >= 1 && (
              <div className="chord-sheet__timing-row chord-sheet__timing-row--interactive">
                {(() => {
                  const minT = lineTimedWords[0].start;
                  const maxT = lineTimedWords[lineTimedWords.length - 1].end;
                  const span = Math.max(0.001, maxT - minT);
                  const lineBpm = Number(bpm ?? rawAnalysis?.bpm);
                  const beatSec = Number.isFinite(lineBpm) && lineBpm > 0 ? (60 / lineBpm) : 0.5;
                  const measureBeats = Number(rawAnalysis?.beatsPerMeasure) > 0 ? Number(rawAnalysis.beatsPerMeasure) : 4;
                  const measureSec = beatSec * measureBeats;
                  const selected = selectedTimingWord?.lineIndex === li
                    ? lineTimedWords.find((w) => Number(w.wordIndex) === Number(selectedTimingWord.wordIndex))
                    : null;
                  const approx = (t) => `${t.toFixed(2)}s`;
                  return (
                    <>
                      {(() => {
                        const ticks = [];
                        const firstBeat = Math.ceil(minT / beatSec) * beatSec;
                        for (let t = firstBeat; t < maxT; t += beatSec) {
                          const leftPct = ((t - minT) / span) * 100;
                          const isMeasure = Math.abs((t / measureSec) - Math.round(t / measureSec)) < 0.02;
                          ticks.push(
                            <span
                              key={`tick-${t.toFixed(3)}`}
                              className={`chord-sheet__timing-tick${isMeasure ? ' chord-sheet__timing-tick--measure' : ''}`}
                              style={{ left: `${leftPct}%` }}
                              title={isMeasure ? `Measure @ ${approx(t)}` : `Beat @ ${approx(t)}`}
                              aria-hidden="true"
                            />,
                          );
                        }
                        return ticks;
                      })()}
                      {lineTimedWords.map((w) => {
                        const leftPct = ((w.start - minT) / span) * 100;
                        const widthPct = (Math.max(0.01, w.end - w.start) / span) * 100;
                        const isSelected = selected && Number(selected.wordIndex) === Number(w.wordIndex);
                        const wordLabel = String(w.word || `w${w.wordIndex}`);
                        return (
                          <div
                            key={`seg-${w.wordIndex}`}
                            className={`chord-sheet__timing-seg${isSelected ? ' chord-sheet__timing-seg--selected' : ''}`}
                            style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                            title={`${w.word || `w${w.wordIndex}`}: ${w.start.toFixed(2)}-${w.end.toFixed(2)}s`}
                            onClick={() => setSelectedTimingWord({ lineIndex: li, wordIndex: Number(w.wordIndex) })}
                          >
                            <span className="chord-sheet__timing-seg-label">{wordLabel}</span>
                          </div>
                        );
                      })}
                      {selected && (
                        <>
                          <span
                            className="chord-sheet__timing-handle chord-sheet__timing-handle--start"
                            style={{ left: `${((selected.start - minT) / span) * 100}%` }}
                            title={`Drag start: ${selected.word || `w${selected.wordIndex}`}`}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              timingUndoSnapshotRef.current = [...(effectiveTimedLyrics || [])].map((x) => ({ ...x }));
                              setDragTimingHandle({
                                lineIndex: li,
                                wordIndex: Number(selected.wordIndex),
                                handle: 'start',
                                min: minT,
                                max: maxT,
                              });
                            }}
                          />
                          <span
                            className="chord-sheet__timing-handle chord-sheet__timing-handle--end"
                            style={{ left: `${((selected.end - minT) / span) * 100}%` }}
                            title={`Drag end: ${selected.word || `w${selected.wordIndex}`}`}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              timingUndoSnapshotRef.current = [...(effectiveTimedLyrics || [])].map((x) => ({ ...x }));
                              setDragTimingHandle({
                                lineIndex: li,
                                wordIndex: Number(selected.wordIndex),
                                handle: 'end',
                                min: minT,
                                max: maxT,
                              });
                            }}
                          />
                        </>
                      )}
                      {timingUndoSnapshotRef.current && (
                        <button
                          type="button"
                          className="chord-sheet__timing-undo"
                          onClick={handleTimingUndo}
                          title="Undo most recent timing edit"
                        >
                          Undo Timing
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
          {lastLyricInOutroByLine[li] && renderInlineChordRows(outroChords || [], -2)}
          </React.Fragment>
        );
      })}
      {!lyricsHasOutroSection && renderChordSection('[Outro]', outroChords, -2)}
      <ChordDiagram
        chord={hoveredChord?.chord}
        anchorRect={hoveredChord?.rect}
        visible={!!hoveredChord}
        time={hoveredChord?.time}
        beatInfo={hoveredChord?.beatInfo}
      />
      {/* Chord alternatives context menu */}
      {altMenu && (
        <div
          className="chord-alt-menu"
          style={{ top: altMenu.y, left: altMenu.x }}
          onMouseLeave={() => setAltMenu(null)}
        >
          <div className="chord-alt-menu__header">Alternatives for <strong>{altMenu.rawChord}</strong></div>
          {altMenu.alts.map((alt, i) => (
            <button
              key={i}
              className="chord-alt-menu__item"
              onClick={() => {
                if (!onAlignmentChange) { setAltMenu(null); return; }
                const newAlignment = (alignment || []).map(a =>
                  a.lineIndex === altMenu.lineIndex && a.wordIndex === altMenu.wordIndex && (a.syllableOffset ?? 0) === (altMenu.syllableOffset ?? 0)
                    ? { ...a, chord: alt.chord }
                    : a
                );
                onAlignmentChange(newAlignment);
                setAltMenu(null);
              }}
            >
              {alt.chord}
              {alt.score != null && (
                <span className="chord-alt-menu__score">{Math.round(alt.score * 100)}%</span>
              )}
            </button>
          ))}
        </div>
      )}
      {wordTimingMenu && (
        <div
          className="word-timing-menu"
          style={{ top: wordTimingMenu.y, left: wordTimingMenu.x }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="word-timing-menu__header">
            Timing for <strong>{wordTimingMenu.word}</strong>
          </div>
          <div className="word-timing-menu__row">
            <span>Start</span>
            <input
              className="word-timing-menu__input"
              type="number"
              step="0.01"
              value={wordTimingMenu.startDraft ?? ''}
              onChange={(e) => setWordTimingMenu((prev) => (prev ? { ...prev, startDraft: e.target.value, error: '' } : prev))}
            />
          </div>
          <div className="word-timing-menu__row">
            <span>End</span>
            <input
              className="word-timing-menu__input"
              type="number"
              step="0.01"
              value={wordTimingMenu.endDraft ?? ''}
              onChange={(e) => setWordTimingMenu((prev) => (prev ? { ...prev, endDraft: e.target.value, error: '' } : prev))}
            />
          </div>
          <div className="word-timing-menu__row">
            <span>Duration</span>
            <span>{formatSecs(Math.max(0, Number(wordTimingMenu.endDraft) - Number(wordTimingMenu.startDraft)))}</span>
          </div>
          {wordTimingMenu.matchedWhisperWord && (
            <div className="word-timing-menu__row">
              <span>Matched</span>
              <span>{wordTimingMenu.matchedWhisperWord}</span>
            </div>
          )}
          {wordTimingMenu.error && (
            <div className="word-timing-menu__error">{wordTimingMenu.error}</div>
          )}
          <div className="word-timing-menu__actions">
            <button type="button" className="word-timing-menu__btn" onClick={applyTimingMenuEdit}>Apply</button>
            <button type="button" className="word-timing-menu__btn word-timing-menu__btn--secondary" onClick={() => setWordTimingMenu(null)}>Cancel</button>
            {timingUndoSnapshotRef.current && (
              <button type="button" className="word-timing-menu__btn word-timing-menu__btn--secondary" onClick={handleTimingUndo}>Undo</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ChordSheetDisplay;
